// js/sections/WheelOfFortuneSection.js
import Delegate from 'ftdomdelegate';

const SVG_NS = 'http://www.w3.org/2000/svg';
const VIEWBOX = 200;
const CENTER = VIEWBOX / 2;
const SLICE_RADIUS = 92;
// The hub covers the middle 23% of the wheel (plus its border).
const HUB_CLEARANCE = 26;

// Dark teal / white / dark blue (brand teal-700 and blue-700), in turn: with three
// colors the white slices never touch.
const PALETTE = ['#16746a', '#ffffff', '#164c74'];
const TEXT_DARK = '#12202b';
const TEXT_LIGHT = '#ffffff';

// Two or three prizes would make huge half or third discs: they are drawn as
// six segments repeating the prizes, the draw itself stays per prize.
const MIN_SEGMENTS = 6;
// From this many segments the slices are narrow: the labels move out towards
// the rim, where a slice is widest.
const NARROW_FROM_SEGMENTS = 8;
// Rows of the slice's own text on the wheel.
const MAX_LABEL_ROWS = 3;
// A name (Cremă) a step smaller than a value (-10%), a value at most this much larger.
const NAME_TO_VALUE_MAX = 1 / 1.15;
const VALUE_TO_NAME_MAX = 1.5;

const SPIN_MS = 5000;
const FULL_TURNS = 5;
// The winning slice is highlighted for a moment before the result shows.
const LANDED_HOLD_MS = 800;
const LANDED_HOLD_REDUCED_MS = 300;
// After a "spin again" slice the other slices light up again after this.
const RETRY_VEIL_MS = 600;
// How far the pointer swings back when a peg pushes it, and over how many degrees.
const FLAPPER_MAX_DEG = 22;
const FLAPPER_CONTACT_MAX_DEG = 10;

const MAX_EXTRA_SPINS = 5;

const REPEAT_AFTER_MIN_MINUTES = 5;
const REPEAT_AFTER_MAX_MINUTES = 1440;
const REPEAT_AFTER_DEFAULT_MINUTES = 60;

// How long to keep waiting for another popup (tombola, cart promo, the cart
// drawer) to close.
const WAIT_FOR_OTHER_MODAL_MS = 2 * 60 * 1000;
const WAIT_POLL_MS = 800;
// The stylesheet is preloaded asynchronously; the popup waits for it.
const WAIT_FOR_STYLES_MS = 8000;
const WAIT_FOR_IMAGES_MS = 800;
const COPIED_FEEDBACK_MS = 2000;
// The labels are fitted again once the wheel changed size by more than this.
const REFIT_MIN_CHANGE_PX = 8;
// A product discount can show up as applicable a moment after it was applied.
const CODE_RECHECK_MS = 1000;
// One column: how far the wheel shrinks for the result, and the gap under it.
const RESULT_WHEEL_MIN_SCALE = 0.3;
const RESULT_WHEEL_MAX_SCALE = 0.62;
const RESULT_WHEEL_GAP = 16;
// The click that closes the drawer can be an add to cart on its way: the cart is
// read this much later, and any cart signal meanwhile takes over.
const DRAWER_CLOSE_SETTLE_MS = 1000;
const REFIT_DEBOUNCE_MS = 150;

// Every request that can change the cart total, whoever sends it.
const CART_MUTATION_RE = /\/cart\/(add|change|update|clear)(\.js|\.json)?(\?|$)/;
// The cart is read for a decision only once no such request has been on its way for
// this long; one that keeps changing for longer decides nothing.
const CART_QUIET_MS = 500;
const CART_SETTLE_MAX_MS = 8000;
const CART_SETTLE_POLL_MS = 100;

/**
 * Cart requests on their way, whoever sends them (the theme, apps, this file).
 * A /cart.js read while an add is still on its way returns the old total: the
 * "almost there" popup then told an amount the cart had already passed.
 * fetch and XMLHttpRequest are wrapped once; the wrappers only count and always
 * hand back the original result.
 */
const cartActivity = { pending: 0, changedAt: -1 };

function trackCartRequests() {
  if (window.__wheelCartTracking) return;
  window.__wheelCartTracking = true;
  const begin = () => {
    cartActivity.pending += 1;
    cartActivity.changedAt = Date.now();
  };
  const end = () => {
    cartActivity.pending = Math.max(0, cartActivity.pending - 1);
    cartActivity.changedAt = Date.now();
  };
  const isCartMutation = (url) => {
    try {
      return CART_MUTATION_RE.test(String(url && url.url ? url.url : url));
    } catch (error) {
      return false;
    }
  };

  const nativeFetch = window.fetch;
  if (typeof nativeFetch === 'function') {
    window.fetch = function (input) {
      const result = nativeFetch.apply(window, arguments);
      if (isCartMutation(input) && result && typeof result.then === 'function') {
        begin();
        result.then(end, end);
      }
      return result;
    };
  }

  const xhr = window.XMLHttpRequest && window.XMLHttpRequest.prototype;
  if (xhr && typeof xhr.open === 'function' && typeof xhr.send === 'function') {
    const nativeOpen = xhr.open;
    const nativeSend = xhr.send;
    xhr.open = function (method, url) {
      this.__wheelCartRequest = isCartMutation(url);
      return nativeOpen.apply(this, arguments);
    };
    xhr.send = function () {
      if (this.__wheelCartRequest) {
        begin();
        this.addEventListener('loadend', end, { once: true });
      }
      return nativeSend.apply(this, arguments);
    };
  }
}

/**
 * Roata norocului.
 *
 * Opens once the cart total reaches the section's amount. The theme announces
 * cart changes in several ways (product:added from product forms and quick add,
 * cart:rerendered from the quantity steppers, cart:refresh / cart:updated from
 * the discount field), and apps add to the cart without announcing anything,
 * so the requests themselves are watched too, through the Resource Timing API.
 * Every signal only schedules a check; the check reads /cart.js, so a missed
 * or a duplicated event can never open the wheel on a stale total.
 *
 * The prize is drawn and stored before the wheel starts turning: closing the
 * popup or reloading the page mid-spin cannot buy a second draw. The result is
 * kept in localStorage per campaign and written into the cart attributes, which
 * puts it on the order in the admin. A "spin again" slice only stores the spin
 * it is owed (capped per campaign), never a result.
 *
 * A won code that was closed without being applied or copied comes back once,
 * on the next page load; after that it lives only in the cart attribute.
 *
 * Every text comes from the section settings through the markup: the script
 * only shows and hides what Liquid rendered, and swaps button labels it reads
 * from data attributes (the cart promo popup's pattern).
 */
class WheelOfFortuneSection extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('.modal__dialog');
    this.wheelElement = this.querySelector('[data-wheel]');
    this.wheelWrap = this.querySelector('.wheel-popup__wheel-wrap');
    this.pointer = this.querySelector('.wheel-popup__pointer');
    this.delegate = new Delegate(this);

    this.options = {
      sectionId: this.dataset.sectionId || '',
      campaign: this.dataset.campaign || 'roata',
      attributeKey: (this.dataset.attributeKey || '').trim(),
      threshold: Math.max(0, parseInt(this.dataset.threshold, 10) || 0),
      // Set: the cart has to hold only this collection's products (empty: any cart).
      collection: Number(this.dataset.collection) || 0,
      delaySeconds: Math.max(0, parseFloat(this.dataset.delay) || 0),
      repeatAfterMinutes: this._clampRepeatAfter(parseInt(this.dataset.repeatAfter, 10)),
      maxRetries: Math.min(MAX_EXTRA_SPINS, Math.max(0, parseInt(this.dataset.maxRetries, 10) || 0))
    };

    this.slices = this._readSlices();
    this.designMode = !!(window.Shopify && window.Shopify.designMode);
    this.storageKeys = {
      result: `wheelOfFortune:${this.options.campaign}`,
      progress: `wheelOfFortuneProgress:${this.options.campaign}`,
      dismissedAt: `wheelOfFortuneDismissed:${this.options.campaign}`
    };
    this._storage = this.designMode ? null : this._resolveStorage();
    this._memoryStorage = {};

    this.rotation = 0;
    this.isSpinning = false;
    this.isBusy = false;
    this.result = this._readStoredResult();
    this.progress = this._readProgress();
    this.isSpun = !!this.result || this.dataset.spun === 'true';

    this._checkTimer = null;
    this._checkToken = 0;
    this._waitTimer = null;
    this._spinTimer = null;
    this._landTimer = null;
    this._feedbackTimer = null;
    this._veilTimer = null;
    this._refitTimer = null;
    this._spinAnimation = null;
    this._spinToken = 0;

    this._onScheduleCheck = this._scheduleCheck.bind(this);
    this._onProductAdded = this._handleProductAdded.bind(this);
    this._onKeydown = this._handleKeydown.bind(this);
    this._onSectionSelect = this._handleSectionSelect.bind(this);
    this._onSectionDeselect = this._handleSectionDeselect.bind(this);
    this._onViewportChange = this._scheduleFit.bind(this);
    this._onCheckoutIntent = this._handleCheckoutIntent.bind(this);
    this._onNudgeKeydown = (event) => {
      if (event.key === 'Escape') this._closeNudge();
    };
    // "Almost there" before the spin; after a won code the same popup markup guards
    // the prize (see _enterGuard). this.nudge is whichever of the two is in use.
    const sectionId = this.options.sectionId ? this._cssEscape(this.options.sectionId) : '';
    this._nudgePopup = sectionId ? document.querySelector(`[data-wheel-nudge="${sectionId}"]`) : null;
    this._guardPopup = sectionId ? document.querySelector(`[data-wheel-guard="${sectionId}"]`) : null;
    this._guarding = false;
    this.nudge = this._nudgePopup;
    [this._nudgePopup, this._guardPopup].forEach((popup) => this._bindPopup(popup));
    // Over the open drawer: its focus trap (a focusin listener on the document) and
    // its outside-click close must not see what happens inside the wheel.
    this.addEventListener('focusin', (event) => {
      if (this._checkoutButton) event.stopPropagation();
    });
    this.addEventListener('click', (event) => {
      if (this._checkoutButton) event.stopPropagation();
    });

    this.delegate.on('click', '[data-action="spin-wheel"]', this._spin.bind(this));
    this.delegate.on('click', '[data-action="close-wheel"]', this._dismiss.bind(this));
    this.delegate.on('click', '[data-action="apply-wheel-code"]', this._applyCode.bind(this));
    this.delegate.on('click', '[data-action="add-wheel-gift"]', this._addGift.bind(this));
    // The chip and the copy button: one handler, one copy.
    this.delegate.on('click', '[data-action="copy-wheel-code"]', this._copyCode.bind(this));
    this.delegate.on('keydown', '.cart-promo-popup__code', (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        this._copyCode(event);
      }
    });
    this.delegate.on('click', this._handleClickOutside.bind(this));
    this._observeSize();

    if (this.designMode) {
      document.addEventListener('shopify:section:select', this._onSectionSelect);
      document.addEventListener('shopify:block:select', this._onSectionSelect);
      document.addEventListener('shopify:section:deselect', this._onSectionDeselect);
      return;
    }

    // Spun on this cart before it kept the won code and gift for the drawer's note.
    if (this.result && this.dataset.prizeUnsaved !== undefined) this._savePrizeToCart(this.result);

    // A won code: from now on the cart is watched for dropping below the amount.
    if (this.result && this.result.code) this._enterGuard();
    if (this._guarding) {
      this._listenToCart();
      this._scheduleCheck(0);
    }

    // A prize closed without being used comes back once, on this page load.
    if (this._owesReopen()) {
      this._reopenWhenFree(Date.now());
      return;
    }

    if (this.isSpun || this.slices.length < 2 || !this._isPlayable()) return;

    // What the page was rendered with counts as the first read of the cart.
    const rendered = parseInt(this.dataset.cartTotal, 10) || 0;
    this._lastCart = { total: rendered, count: rendered > 0 ? 1 : 0, readAt: 0 };
    this._listenToCart();
    this._observeDrawerClose();

    // A full page load after a form post (cart page, a product form without
    // JS) changes the cart without any event: the server-rendered total says
    // whether a check is worth the request.
    const renderedTotal = parseInt(this.dataset.cartTotal, 10) || 0;
    if (renderedTotal > 0 && renderedTotal >= this.options.threshold) {
      this._scheduleCheck(Math.max(this.options.delaySeconds, 1.5) * 1000);
    }
  }

  disconnectedCallback() {
    this.delegate.off();
    document.documentElement.removeEventListener('product:added', this._onProductAdded);
    document.removeEventListener('click', this._onCheckoutIntent, true);
    document.removeEventListener('keydown', this._onNudgeKeydown);
    document.removeEventListener('cart:rerendered', this._onScheduleCheck, true);
    document.removeEventListener('cart:updated', this._onScheduleCheck);
    document.documentElement.removeEventListener('cart:refresh', this._onScheduleCheck);
    document.removeEventListener('shopify:section:select', this._onSectionSelect);
    document.removeEventListener('shopify:block:select', this._onSectionSelect);
    document.removeEventListener('shopify:section:deselect', this._onSectionDeselect);
    document.removeEventListener('keydown', this._onKeydown);
    window.removeEventListener('resize', this._onViewportChange);
    if (this._resourceObserver) this._resourceObserver.disconnect();
    if (this._sizeObserver) this._sizeObserver.disconnect();
    if (this._drawerObserver) this._drawerObserver.disconnect();
    if (this._drawerCloseObserver) this._drawerCloseObserver.disconnect();
    this._stopSpin();
    window.clearTimeout(this._checkTimer);
    window.clearTimeout(this._waitTimer);
    window.clearTimeout(this._nudgeTimer);
    window.clearTimeout(this._feedbackTimer);
    window.clearTimeout(this._stylesTimer);
    window.clearTimeout(this._veilTimer);
    window.clearTimeout(this._refitTimer);
    if (this.getAttribute('aria-hidden') === 'false') this._unlockPage();
  }

  /* ------------------------------------------------------- data ------- */

  _readSlices() {
    const script = this.querySelector('[data-wheel-slices]');
    if (!script) return [];
    try {
      const slices = JSON.parse(script.textContent);
      return Array.isArray(slices) ? slices.map((slice) => ({
        id: String(slice.id || ''),
        title: String(slice.title || ''),
        label: String(slice.label || '').split(/\r?\n/).map((row) => row.trim()).filter(Boolean).slice(0, MAX_LABEL_ROWS),
        image: String(slice.image || ''),
        code: String(slice.code || '').trim(),
        weight: Math.max(0, Number(slice.weight) || 0),
        color: String(slice.color || ''),
        retry: slice.retry === true,
        gift: slice.gift === true,
        productTitle: String(slice.productTitle || '')
      })) : [];
    } catch (error) {
      return [];
    }
  }

  _clampRepeatAfter(minutes) {
    if (!Number.isFinite(minutes)) return REPEAT_AFTER_DEFAULT_MINUTES;
    return Math.min(REPEAT_AFTER_MAX_MINUTES, Math.max(REPEAT_AFTER_MIN_MINUTES, minutes));
  }

  /**
   * What landing on a slice means. With no extra spins allowed a "spin again"
   * slice is a plain "no prize" slice, and its code is ignored either way.
   */
  _kind(slice) {
    if (slice.retry && this.options.maxRetries > 0) return 'retry';
    if (slice.gift && !slice.retry) return 'gift';
    if (slice.code && !slice.retry) return 'prize';
    return 'none';
  }

  /** At least one real outcome (prize or no prize) can win. */
  _isPlayable() {
    return this.slices.reduce((sum, slice) => sum + (this._kind(slice) === 'retry' ? 0 : slice.weight), 0) > 0;
  }

  /* ---------------------------------------------------- storage ------- */

  _resolveStorage() {
    try {
      const storage = window.localStorage;
      const testKey = '__wheel_of_fortune_test__';
      storage.setItem(testKey, testKey);
      storage.removeItem(testKey);
      return storage;
    } catch (error) {
      return null;
    }
  }

  _getItem(key) {
    if (this._storage) return this._storage.getItem(key);
    return this._memoryStorage[key] || null;
  }

  _setItem(key, value) {
    if (this._storage) {
      try {
        this._storage.setItem(key, value);
        return;
      } catch (error) {
        // Quota or a privacy mode: fall back to memory for this page view.
      }
    }
    this._memoryStorage[key] = value;
  }

  _readStoredResult() {
    try {
      const stored = JSON.parse(this._getItem(this.storageKeys.result) || 'null');
      return stored && typeof stored === 'object' ? stored : null;
    } catch (error) {
      return null;
    }
  }

  /** Extra spins already granted by "spin again" slices, and the last one hit. */
  _readProgress() {
    try {
      const stored = JSON.parse(this._getItem(this.storageKeys.progress) || 'null');
      if (stored && typeof stored === 'object') {
        return {
          retries: Math.max(0, parseInt(stored.retries, 10) || 0),
          last: stored.last ? String(stored.last) : null
        };
      }
    } catch (error) {
      // A broken entry counts as no progress.
    }
    return { retries: 0, last: null };
  }

  _storeResult() {
    if (this.designMode) return;
    this._setItem(this.storageKeys.result, JSON.stringify(this.result));
  }

  _storeProgress() {
    this._setItem(this.storageKeys.progress, JSON.stringify(this.progress));
  }

  /** Closing without a spin keeps the wheel quiet for a while. */
  _isDismissSuppressed() {
    const dismissedAt = Number.parseInt(this._getItem(this.storageKeys.dismissedAt) || '', 10);
    if (!Number.isFinite(dismissedAt)) return false;
    const elapsed = Date.now() - dismissedAt;
    // A clock change must not keep it closed forever.
    if (elapsed < 0) return false;
    return elapsed < this.options.repeatAfterMinutes * 60 * 1000;
  }

  /** The visitor already has the code: applied in the cart or copied. */
  _codeIsSafe(result = this.result) {
    return !result || (!result.code && !result.gift) || !!result.applied || !!result.copied;
  }

  /**
   * A won code closed without being applied or copied: the popup shows it once
   * more on the next page load. An empty cart (the order is placed) ends it.
   */
  _owesReopen() {
    const result = this.result;
    return !!result && result.reopen === 'pending' && !this._codeIsSafe(result) &&
      (parseInt(this.dataset.cartTotal, 10) || 0) > 0;
  }

  async _reopenWhenFree(startedAt) {
    window.clearTimeout(this._waitTimer);
    if (!this._owesReopen() || this.getAttribute('aria-hidden') === 'false') return;
    if (!this._otherOverlayOpen() && (await this._whenStylesReady()) && !this._otherOverlayOpen()) {
      await this._preloadImages();
      if (!this._owesReopen() || this._otherOverlayOpen()) return;
      this.open();
      // Shown now: never again.
      this.result.reopen = 'done';
      this._storeResult();
      return;
    }
    if (Date.now() - startedAt > WAIT_FOR_OTHER_MODAL_MS) return;
    this._waitTimer = window.setTimeout(() => this._reopenWhenFree(startedAt), WAIT_POLL_MS);
  }

  /** Every way the cart changes, and the checkout click; attached once. */
  _listenToCart() {
    if (this._listening) return;
    this._listening = true;
    trackCartRequests();
    document.documentElement.addEventListener('product:added', this._onProductAdded);
    // Ahead of the drawer's own capture listener, which starts its checkout (and upsell).
    document.addEventListener('click', this._onCheckoutIntent, true);
    // cart:rerendered is dispatched on the cart element itself and does not
    // bubble, so it is caught on the way down.
    document.addEventListener('cart:rerendered', this._onScheduleCheck, true);
    document.addEventListener('cart:updated', this._onScheduleCheck);
    document.documentElement.addEventListener('cart:refresh', this._onScheduleCheck);
    this._observeCartRequests();
  }

  _bindPopup(popup) {
    // Bound once: the section can reconnect in the theme editor.
    if (!popup || popup._wheelBound) return;
    popup._wheelBound = true;
    popup.addEventListener('click', (event) => {
      // Over the open drawer (a checkout click, or the prize guard): its outside-click close must not see this.
      if (this._nudgeCheckoutButton || this._guarding) event.stopPropagation();
      const dialog = popup.querySelector('.modal__dialog');
      if (event.target.closest('[data-action="nudge-checkout"]')) {
        event.preventDefault();
        this._closeNudge(true);
      } else if (event.target.closest('[data-action="close-wheel-nudge"]') || (dialog && !dialog.contains(event.target))) {
        event.preventDefault();
        this._closeNudge(false);
      }
    });
    // ...nor its focus trap (a focusin listener on the document).
    popup.addEventListener('focusin', (event) => {
      if (this._nudgeCheckoutButton || this._guarding) event.stopPropagation();
    });
  }

  /* ---------------------------------------------------- trigger ------- */

  _handleProductAdded(event) {
    // The event fires for a refused add as well; only a real add counts.
    if (!event.detail || event.detail.productAdded !== true) return;
    this._scheduleCheck();
  }

  _observeCartRequests() {
    if (!('PerformanceObserver' in window)) return;
    try {
      this._resourceObserver = new PerformanceObserver((list) => {
        const changed = list.getEntries().some((entry) =>
          (entry.initiatorType === 'fetch' || entry.initiatorType === 'xmlhttprequest') &&
          CART_MUTATION_RE.test(entry.name)
        );
        if (changed) this._scheduleCheck();
      });
      this._resourceObserver.observe({ type: 'resource' });
    } catch (error) {
      this._resourceObserver = null;
    }
  }

  _canShow() {
    // Spun in another tab since this page loaded.
    if (!this.isSpun && !this.designMode) {
      const stored = this._readStoredResult();
      if (stored) {
        this.result = stored;
        this.isSpun = true;
      }
    }
    return !this.isSpun &&
      this.slices.length > 1 &&
      (this.designMode || this._isPlayable()) &&
      this.getAttribute('aria-hidden') !== 'false' &&
      !this._isDismissSuppressed();
  }

  /**
   * Several signals arrive for one add; the shared timer collapses them. A newer
   * cart state decides afresh, so a wait started for an older one ends here.
   */
  _scheduleCheck(delayMs) {
    if (this._guarding) {
      this._scheduleGuard(delayMs);
      return;
    }
    if (!this._canShow()) return;
    const delay = typeof delayMs === 'number' ? delayMs : this.options.delaySeconds * 1000;
    const token = ++this._checkToken;
    window.clearTimeout(this._checkTimer);
    window.clearTimeout(this._waitTimer);
    this._checkTimer = window.setTimeout(() => this._checkCart(token), delay);
  }

  _qualifies(cart) {
    return !!cart && Number(cart.item_count || 0) > 0 && Number(cart.total_price || 0) >= this.options.threshold;
  }

  async _checkCart(token) {
    if (!this._canShow()) return;
    const cart = await this._settledCart();
    // Still changing: the request that changes it brings its own check.
    if (!cart || token !== this._checkToken || !this._canShow()) return;
    // "Almost there" follows the cart: shown, updated, or closed once the wheel is due.
    this._maybeNudge(cart, token);
    if (!this._qualifies(cart)) {
      this._pendingOpen = false;
      return;
    }
    // Only visitors who qualify load the slice images, and the wheel
    // never opens with half of them missing.
    await this._preloadImages();
    // Owed from now on: a checkout click in the drawer brings it up (see _handleCheckoutIntent).
    this._pendingOpen = true;
    this._openWhenFree(Date.now(), token);
  }

  /** Another popup, or the cart drawer, is open (or holds the page lock). */
  _otherOverlayOpen() {
    const otherModal = Array.from(document.querySelectorAll('.modal[aria-hidden="false"], .cart-upsell-modal[aria-hidden="false"]'))
      .some((modal) => modal !== this);
    if (otherModal || document.querySelector('cos-side-cart[aria-hidden="false"]')) return true;
    return this.getAttribute('aria-hidden') !== 'false' && document.documentElement.classList.contains('is-locked');
  }

  /**
   * Never stacks on another popup or the cart drawer: waits for them to close
   * first. After a wait the cart is read again, it may have changed meanwhile.
   */
  async _openWhenFree(startedAt, token, waited = false) {
    window.clearTimeout(this._waitTimer);
    if (token !== this._checkToken || !this._canShow()) return;
    if (!this._otherOverlayOpen()) {
      if (!(await this._whenStylesReady())) return;
      if (waited && !this._qualifies(await this._settledCart())) {
        this._pendingOpen = false;
        return;
      }
      // Something may have opened meanwhile, or a newer check took over.
      if (token !== this._checkToken || !this._canShow()) return;
      if (!this._otherOverlayOpen()) {
        this.open();
        return;
      }
    }
    if (Date.now() - startedAt > WAIT_FOR_OTHER_MODAL_MS) return;
    this._waitTimer = window.setTimeout(() => this._openWhenFree(startedAt, token, true), WAIT_POLL_MS);
  }

  _stylesReady() {
    return window.getComputedStyle(this).getPropertyValue('--wheel-css').trim() === '1';
  }

  /** Resolves true once wheel-of-fortune.css applies, false after maxMs. */
  _whenStylesReady(maxMs = WAIT_FOR_STYLES_MS) {
    if (this._stylesReady()) return Promise.resolve(true);
    const startedAt = Date.now();
    return new Promise((resolve) => {
      const poll = () => {
        if (this._stylesReady()) {
          resolve(true);
        } else if (Date.now() - startedAt >= maxMs || !this.isConnected) {
          resolve(false);
        } else {
          this._stylesTimer = window.setTimeout(poll, 100);
        }
      };
      poll();
    });
  }

  _preloadImages(maxMs = WAIT_FOR_IMAGES_MS) {
    // The background photo for this screen too: the stylesheet only asks for it once the popup is open.
    const phone = window.matchMedia && window.matchMedia('(max-width: 640px)').matches;
    const background = phone ? this.dataset.bgImageMobile || this.dataset.bgImage : this.dataset.bgImage;
    const urls = Array.from(new Set([...this.slices.map((slice) => slice.image), background].filter(Boolean)));
    if (!urls.length) return Promise.resolve();
    const decoded = Promise.all(urls.map((src) => {
      const image = new Image();
      image.src = src;
      return image.decode ? image.decode().catch(() => {}) : Promise.resolve();
    }));
    return Promise.race([decoded, new Promise((resolve) => window.setTimeout(resolve, maxMs))]);
  }

  _cartRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  _cartUrl() {
    return window.routes && window.routes.cartUrl ? window.routes.cartUrl : `${this._cartRoot()}cart`;
  }

  async _fetchCart() {
    try {
      const response = await fetch(`${this._cartUrl()}.js`, {
        credentials: 'same-origin',
        headers: { 'Cache-Control': 'no-cache', 'X-Requested-With': 'XMLHttpRequest' }
      });
      if (!response.ok) return null;
      return await response.json();
    } catch (error) {
      return null;
    }
  }

  /**
   * What the wheel goes by, as { total_price, item_count }: the cart, or with a
   * collection set nothing at all as soon as one product is not from it.
   */
  async _readCounted() {
    const readAt = Date.now();
    let counted = null;
    if (this.options.collection) {
      counted = await this._fetchCollectionLines();
    } else {
      const cart = await this._fetchCart();
      if (cart) counted = { total_price: Number(cart.total_price || 0), item_count: Number(cart.item_count || 0) };
    }
    // What the checkout click decides on when nothing changed the cart since.
    if (counted) this._lastCart = { total: counted.total_price, count: counted.item_count, readAt };
    return counted;
  }

  /** The cart when every line is from the collection, else an empty one (templates/cart.wheel.liquid). */
  async _fetchCollectionLines() {
    try {
      // No Accept: application/json: with it Shopify answers with its own cart JSON, not the view.
      const response = await fetch(`${this._cartUrl()}?view=wheel`, {
        credentials: 'same-origin',
        headers: { 'Cache-Control': 'no-cache' }
      });
      if (!response.ok) return null;
      const lines = JSON.parse(await response.text()).items || [];
      if (lines.some((line) => !(line.collections || []).includes(this.options.collection))) {
        return { total_price: 0, item_count: 0 };
      }
      return lines.reduce((counted, line) => ({
        total_price: counted.total_price + Number(line.line || 0),
        item_count: counted.item_count + Number(line.quantity || 0)
      }), { total_price: 0, item_count: 0 });
    } catch (error) {
      return null;
    }
  }

  /** No cart request on its way since a read that began at readAt. */
  _isCurrent(readAt) {
    return cartActivity.pending === 0 && cartActivity.changedAt < readAt;
  }

  /**
   * What counts in the cart (see _readCounted) once nothing changes it any more:
   * no cart request on its way for a moment before the read, and none during
   * it. Null when it keeps changing.
   */
  async _settledCart(read = () => this._readCounted()) {
    const startedAt = Date.now();
    while (Date.now() - startedAt < CART_SETTLE_MAX_MS) {
      const quiet = cartActivity.pending === 0 && Date.now() - cartActivity.changedAt >= CART_QUIET_MS;
      if (quiet) {
        const readAt = Date.now();
        const cart = await read();
        if (!cart) return null;
        if (this._isCurrent(readAt)) {
          this._settledAt = readAt;
          return cart;
        }
      }
      await new Promise((resolve) => window.setTimeout(resolve, CART_SETTLE_POLL_MS));
    }
    return null;
  }

  /* ------------------------------------------------- open/close ------- */

  open() {
    if (this.getAttribute('aria-hidden') === 'false') return;
    this._pendingOpen = false;
    // From now on the background photo applies (it stays while the popup fades out).
    this.classList.add('is-opened');
    if (!this.designMode) this.progress = this._readProgress();
    // Sized while still hidden, so the popup appears at its final size.
    this._fitWheel();
    this._buildWheel();

    if (this.result) {
      this._showResult(this.result, false);
    } else if (this.progress.retries > 0 && this.progress.last) {
      this._showRetry(this.slices.findIndex((slice) => slice.id === this.progress.last), false);
    } else {
      this._setRetry(false);
      this._setState('intro');
    }

    this._previousFocus = document.activeElement;
    this._lockPage();
    this.setAttribute('aria-hidden', 'false');
    document.addEventListener('keydown', this._onKeydown);
    window.addEventListener('resize', this._onViewportChange);

    // The dialog itself takes the focus, as modal-manager's trapFocus does:
    // no focus ring on a button nobody tabbed to. Tab reaches the buttons.
    const focusIn = () => {
      if (this.getAttribute('aria-hidden') !== 'false' || this.dialog.contains(document.activeElement)) return;
      this.dialog.focus({ preventScroll: true });
    };
    window.requestAnimationFrame(focusIn);
    window.setTimeout(focusIn, 260);
    this._watchDrawer();
  }

  close() {
    if (this.getAttribute('aria-hidden') !== 'false') return;
    if (this._drawerObserver) this._drawerObserver.disconnect();
    this.setAttribute('aria-hidden', 'true');
    document.removeEventListener('keydown', this._onKeydown);
    window.removeEventListener('resize', this._onViewportChange);
    this._unlockPage();
    this._announceAddedProduct();
    this._resumeCheckout();
    if (this._previousFocus && typeof this._previousFocus.focus === 'function' && document.contains(this._previousFocus)) {
      this._previousFocus.focus({ preventScroll: true });
    }
    this._previousFocus = null;
  }

  /**
   * The drawer can still open after the wheel (its section response may come
   * after the check). A wheel not played yet steps aside without counting as
   * closed, and comes back once the drawer is closed again.
   */
  _watchDrawer() {
    const drawer = document.querySelector('cos-side-cart');
    if (this.designMode || !drawer || !('MutationObserver' in window)) return;
    if (this._drawerObserver) this._drawerObserver.disconnect();
    this._drawerObserver = new MutationObserver(() => {
      if (drawer.getAttribute('aria-hidden') !== 'false' || this.getAttribute('aria-hidden') !== 'false') return;
      const untouched = this.dataset.state === 'intro' && !this.dataset.retry && !this.result && !this.isSpinning;
      if (!untouched) return;
      this.close();
      this._scheduleCheck(0);
    });
    this._drawerObserver.observe(drawer, { attributes: true, attributeFilter: ['aria-hidden'] });
  }

  /**
   * "Finalizează comanda" in the cart drawer while the wheel still waits for the
   * drawer to close (the visitor never closes it): the wheel shows first, over
   * the drawer, and the click is replayed once it closes, so the drawer's own
   * checkout path (its upsell included) runs as usual.
   */
  _handleCheckoutIntent(event) {
    if (this._replayingCheckout) return;
    const button = event.target && event.target.closest ? event.target.closest('#mini-cart button[name="checkout"]') : null;
    if (!button) return;
    if (this._guarding) {
      // Known to be fine (at the amount, or no won code on it): checkout as usual.
      const known = this._guardState && this._isCurrent(this._guardState.readAt) ? this._guardState : null;
      if (known && (!known.below || !known.applied)) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      this._guardCheckout(button);
      return;
    }
    if (this._pendingOpen && this._canShow() && this._stylesReady()) {
      event.preventDefault();
      event.stopImmediatePropagation();
      window.clearTimeout(this._waitTimer);
      this._checkoutButton = button;
      this._setCheckoutLabel(true);
      this.open();
      return;
    }
    // Short of the wheel: "almost there" first, with a way on to checkout. A cart
    // known to be out of range goes straight on; one that may have changed since
    // it was read is read again first, and decides then.
    if (!this._nudgeActive()) return;
    const known = this._lastCart && this._isCurrent(this._lastCart.readAt) ? this._lastCart : null;
    if (known && !this._nudgeEligible(known.total, known.count)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    window.clearTimeout(this._nudgeTimer);
    this._decideCheckout(button);
  }

  async _decideCheckout(button) {
    const cart = await this._settledCart();
    const total = cart ? Number(cart.total_price || 0) : 0;
    if (cart && this._qualifies(cart) && this._canShow() && this._stylesReady()) {
      window.clearTimeout(this._waitTimer);
      this._checkoutButton = button;
      this._setCheckoutLabel(true);
      this.open();
    } else if (cart && this._nudgeEligible(total, Number(cart.item_count || 0))) {
      this._nudgeCheckoutButton = button;
      this._fillNudge(this.options.threshold - total);
      this._setNudgeCheckout(true);
      this._openNudge(this._cartKey(cart));
    } else {
      this._replayCheckout(button);
    }
  }

  _resumeCheckout() {
    const button = this._checkoutButton;
    if (!button) return;
    this._checkoutButton = null;
    this._setCheckoutLabel(false);
    this._replayCheckout(button);
  }

  /** The drawer's own checkout path takes over (its upsell included). */
  _replayCheckout(button) {
    this._replayingCheckout = true;
    window.setTimeout(() => {
      if (document.contains(button)) {
        button.click();
      } else {
        window.location.assign(`${this._cartRoot()}checkout`);
      }
      this._replayingCheckout = false;
    }, 0);
  }

  /** The "no prize" button leads on to checkout when the wheel came from there. */
  _setCheckoutLabel(on) {
    const button = this.querySelector('.wheel-popup__panel--lost [data-action="close-wheel"]');
    if (!button || !button.dataset.checkoutLabel) return;
    if (!button.dataset.label) button.dataset.label = button.textContent.trim();
    button.textContent = on ? button.dataset.checkoutLabel : button.dataset.label;
  }

  /* ------------------------------------------------ prize guard ----- */

  /**
   * A won code is for carts at the wheel's amount. Taking products out after the
   * spin would keep it, so below the amount the code comes off and the guard
   * popup (the "almost there" markup) says how much to add; back at the amount
   * the code goes back on. Shopify's own minimum purchase amount on the code is
   * what also holds at checkout, this only keeps the cart honest.
   */
  _enterGuard() {
    if (this._guarding || this.designMode || !this._guardPopup || !this.result || !this.result.code) return;
    this._guarding = true;
    this.nudge = this._guardPopup;
  }

  _scheduleGuard(delayMs) {
    const delay = typeof delayMs === 'number' ? delayMs : this.options.delaySeconds * 1000;
    const token = ++this._checkToken;
    window.clearTimeout(this._checkTimer);
    this._checkTimer = window.setTimeout(() => this._runGuard(token), delay);
  }

  /**
   * The cart without what the wheel gave: the code's own discount added back
   * and the gift line left out, so neither counts toward the amount.
   */
  _prizeState(cart) {
    const code = this.result.code.toLowerCase();
    const same = (title) => String(title || '').trim().toLowerCase() === code;
    const gift = this._prizeAttributes(this.result)._wheel_gift;
    let base = Number(cart.total_price || 0);
    let count = 0;
    (cart.items || []).forEach((item) => {
      const off = (item.line_level_discount_allocations || []).reduce((sum, allocation) =>
        sum + (allocation && allocation.discount_application && same(allocation.discount_application.title) ? Number(allocation.amount || 0) : 0), 0);
      base += off;
      if (gift && String(item.variant_id) === gift) {
        base -= Number(item.final_line_price || 0) + off;
      } else {
        count += Number(item.quantity || 0);
      }
    });
    (cart.cart_level_discount_applications || []).forEach((application) => {
      if (application && same(application.title)) base += Number(application.total_allocated_amount || 0);
    });
    return {
      base,
      count,
      key: `${base}:${count}`,
      below: base < this.options.threshold,
      applied: this._codeApplied(cart, this.result.code),
      // On the cart even where Shopify does not apply it (its own minimum amount).
      attached: (cart.discount_codes || []).some((entry) => entry && same(entry.code))
    };
  }

  async _runGuard(token) {
    const cart = await this._settledCart(() => this._fetchCart());
    if (!cart || token !== this._checkToken) return;
    const state = await this._enforceGuard(cart);
    if (token !== this._checkToken) return;
    if (state.below) {
      this._showGuard(state);
    } else if (this.nudge.getAttribute('aria-hidden') === 'false' && !this._nudgeCheckoutButton) {
      this._closeNudge(false);
    }
  }

  /** Takes the code off below the amount, puts it back once the cart reaches it again. */
  async _enforceGuard(cart) {
    const state = { ...this._prizeState(cart), readAt: this._settledAt || 0 };
    this._guardState = state;
    if (state.below && state.applied) {
      await this._setPrizeCode(cart, false);
      this.result.guardRemoved = true;
      this._storeResult();
      this._refreshDrawer();
      state.applied = false;
      state.attached = false;
    } else if (!state.below && this.result.guardRemoved) {
      if (!state.attached && state.count > 0) {
        await this._setPrizeCode(cart, true);
        this._refreshDrawer();
      }
      this.result.guardRemoved = false;
      this._storeResult();
    }
    return state;
  }

  /** Below the amount with a won code on the cart, or one the guard took off: once per cart. */
  _showGuard(state) {
    if (state.count === 0 || !(state.attached || this.result.guardRemoved)) return;
    const remaining = this.options.threshold - state.base;
    if (this.nudge.getAttribute('aria-hidden') === 'false') {
      this._fillNudge(remaining);
      return;
    }
    if (state.key === this._nudgeShownFor) return;
    // Over the drawer, where the products came out, but never over the wheel or another popup.
    const modalOpen = Array.from(document.querySelectorAll('.modal[aria-hidden="false"], .cart-upsell-modal[aria-hidden="false"]'))
      .some((modal) => modal !== this.nudge);
    if (modalOpen) return;
    this._fillNudge(remaining);
    this._openNudge(state.key);
  }

  /** Checkout from the drawer: below the amount the code comes off first, the popup offers to go on without it. */
  async _guardCheckout(button) {
    const cart = await this._settledCart(() => this._fetchCart());
    const state = cart ? await this._enforceGuard(cart) : null;
    if (!state || !state.below || state.count === 0 || !this.result.guardRemoved) {
      this._replayCheckout(button);
      return;
    }
    this._nudgeCheckoutButton = button;
    this._fillNudge(this.options.threshold - state.base);
    this._setNudgeCheckout(true);
    this._openNudge(state.key);
  }

  /** Sets the cart's codes: the others stay as they are, the won one goes off or on. */
  async _setPrizeCode(cart, on) {
    const code = this.result.code;
    const others = (cart.discount_codes || [])
      .map((entry) => entry && entry.code)
      .filter((value) => value && value.toLowerCase() !== code.toLowerCase());
    try {
      await fetch(`${this._cartUrl()}/update.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ discount: (on ? others.concat(code) : others).join(',') })
      });
    } catch (error) {
      // Left as it was; the next cart change tries again.
    }
  }

  /** The drawer renders the new prices and codes, as after its own code field. */
  _refreshDrawer() {
    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { scrollToTop: false } }));
  }

  /* ------------------------------------------------ almost there ---- */

  /**
   * Between the "almost there" amount and the wheel's: the gift promo popup's
   * markup tells how much is left, after every cart check, never on top of
   * another popup or the drawer.
   */
  _maybeNudge(cart, token) {
    if (!cart || !this.nudge) return;
    const total = Number(cart.total_price || 0);
    const eligible = this._nudgeEligible(total, Number(cart.item_count || 0));
    if (this.nudge.getAttribute('aria-hidden') === 'false') {
      // Already up (opened on an older cart): it tells the current amount, or goes.
      if (eligible) this._fillNudge(this.options.threshold - total);
      else if (!this._nudgeCheckoutButton) this._closeNudge(false);
      return;
    }
    // Once per cart: other signals of the same change, or the drawer closing on a
    // cart it was already shown for, do not bring it back.
    if (!eligible || this._cartKey(cart) === this._nudgeShownFor) {
      window.clearTimeout(this._nudgeTimer);
      return;
    }
    this._openNudgeWhenFree(Date.now(), token);
  }

  /** What tells one cart from another for the "almost there" popup. */
  _cartKey(cart) {
    return `${Number(cart.total_price || 0)}:${Number(cart.item_count || 0)}`;
  }

  /** The popup is set up and the wheel not played yet. */
  _nudgeActive() {
    return !!this.nudge && !this.designMode && !this.isSpun && !this._readStoredResult() && (parseInt(this.nudge.dataset.from, 10) || 0) > 0;
  }

  /** The cart sits between the "almost there" amount and the wheel's. */
  _nudgeEligible(total, count) {
    return this._nudgeActive() && count > 0 && total >= parseInt(this.nudge.dataset.from, 10) && total < this.options.threshold;
  }

  /** The drawer closing is another moment to tell what is left (the cart promo popup waits for it too). */
  _observeDrawerClose() {
    const drawer = document.querySelector('cos-side-cart');
    if (!this.nudge || this.designMode || !drawer || !('MutationObserver' in window)) return;
    let open = drawer.getAttribute('aria-hidden') === 'false';
    this._drawerCloseObserver = new MutationObserver(() => {
      const nowOpen = drawer.getAttribute('aria-hidden') === 'false';
      const closed = open && !nowOpen;
      open = nowOpen;
      if (!closed || this._checkoutButton || this._nudgeCheckoutButton) return;
      this._scheduleCheck(DRAWER_CLOSE_SETTLE_MS);
    });
    this._drawerCloseObserver.observe(drawer, { attributes: true, attributeFilter: ['aria-hidden'] });
  }

  /** The "go to checkout" button shows only when the popup came from a checkout click. */
  _setNudgeCheckout(on) {
    const button = this.nudge && this.nudge.querySelector('[data-action="nudge-checkout"]');
    if (button) button.hidden = !on;
  }

  /** The gift promo popup's {remaining}, formatted the same way. */
  _fillNudge(remainingCents) {
    const amount = window.Shopify && typeof window.Shopify.formatMoney === 'function'
      ? window.Shopify.formatMoney(remainingCents, window.theme && window.theme.moneyFormat)
      : `${(remainingCents / 100).toFixed(2)}`;
    this.nudge.querySelectorAll('[data-remaining-text]').forEach((element) => {
      const [before, ...rest] = (element.getAttribute('data-remaining-text') || '').split('{remaining}');
      element.textContent = before;
      if (!rest.length) return;
      const strong = document.createElement('span');
      strong.className = 'gift-promo-popup__amount';
      strong.textContent = amount;
      element.append(strong, rest.join('{remaining}'));
    });
  }

  /**
   * Right before it shows, the cart is read again once settled: the amount
   * comes from that read, and a cart that reached the wheel meanwhile shows nothing.
   */
  async _openNudgeWhenFree(startedAt, token) {
    window.clearTimeout(this._nudgeTimer);
    if (token !== this._checkToken) return;
    if (!this._otherOverlayOpen()) {
      const cart = await this._settledCart();
      if (token !== this._checkToken || !cart) return;
      const total = Number(cart.total_price || 0);
      const key = this._cartKey(cart);
      if (!this._nudgeEligible(total, Number(cart.item_count || 0)) || key === this._nudgeShownFor) return;
      if (!this._otherOverlayOpen()) {
        this._fillNudge(this.options.threshold - total);
        this._openNudge(key);
        return;
      }
    }
    if (Date.now() - startedAt > WAIT_FOR_OTHER_MODAL_MS) return;
    this._nudgeTimer = window.setTimeout(() => this._openNudgeWhenFree(startedAt, token), WAIT_POLL_MS);
  }

  _openNudge(cartKey) {
    if (!this.nudge || this.nudge.getAttribute('aria-hidden') === 'false') return;
    this._nudgeShownFor = cartKey;
    this._nudgeFocus = document.activeElement;
    this._lockPage();
    this.nudge.setAttribute('aria-hidden', 'false');
    document.addEventListener('keydown', this._onNudgeKeydown);
    const dialog = this.nudge.querySelector('.modal__dialog');
    if (dialog) window.requestAnimationFrame(() => dialog.focus({ preventScroll: true }));
  }

  /** proceed: "Finalizează comanda" after a checkout click; anything else stays on the page. */
  _closeNudge(proceed = false) {
    if (!this.nudge || this.nudge.getAttribute('aria-hidden') !== 'false') return;
    const checkoutButton = this._nudgeCheckoutButton;
    this._nudgeCheckoutButton = null;
    this.nudge.setAttribute('aria-hidden', 'true');
    document.removeEventListener('keydown', this._onNudgeKeydown);
    this._unlockPage();
    if (this._nudgeFocus && typeof this._nudgeFocus.focus === 'function' && document.contains(this._nudgeFocus)) {
      this._nudgeFocus.focus({ preventScroll: true });
    }
    this._nudgeFocus = null;
    this._setNudgeCheckout(false);
    if (proceed && checkoutButton) this._replayCheckout(checkoutButton);
  }

  /** Same lock as modal-manager.js: the page stays where the visitor left it. */
  _lockPage() {
    const root = document.documentElement;
    this._ownsLock = !root.classList.contains('is-locked');
    if (!this._ownsLock) return;
    this._scrollY = window.scrollY || 0;
    const scrollbarGap = window.innerWidth - root.clientWidth;
    document.body.style.top = `-${this._scrollY}px`;
    if (scrollbarGap > 0) document.body.style.paddingRight = `${scrollbarGap}px`;
    root.classList.add('is-locked');
  }

  _unlockPage() {
    if (!this._ownsLock) return;
    this._ownsLock = false;
    document.documentElement.classList.remove('is-locked');
    document.body.style.top = '';
    document.body.style.paddingRight = '';
    window.scrollTo({ top: this._scrollY || 0, behavior: 'instant' });
  }

  _dismiss(event) {
    if (event) event.preventDefault();
    if (this.isSpinning || this.dataset.state === 'landed') return;
    if (!this.isSpun) this._setItem(this.storageKeys.dismissedAt, String(Date.now()));
    // A code that was neither applied nor copied is shown once more later.
    if (this.dataset.state === 'won' && this.result && !this._codeIsSafe() && !this.result.reopen) {
      this.result.reopen = 'pending';
      this._storeResult();
    }
    this.close();
  }

  _handleClickOutside(event) {
    if (this.getAttribute('aria-hidden') !== 'false') return;
    if (this.dialog && this.dialog.contains(event.target)) return;
    // A stray tap next to the popup must not throw a prize away.
    if (['spinning', 'landed', 'won'].includes(this.dataset.state)) return;
    this._dismiss();
  }

  _handleKeydown(event) {
    if (event.key === 'Escape') {
      // Escape skips the wait instead of closing mid-spin.
      if (this.dataset.state === 'spinning') {
        if (this._spinAnimation) this._spinAnimation.finish();
        return;
      }
      if (this.dataset.state === 'landed') {
        this._reveal();
        return;
      }
      this._dismiss();
      return;
    }
    if (event.key === 'Tab') this._trapFocus(event);
  }

  _trapFocus(event) {
    const focusable = Array.from(this.dialog.querySelectorAll('button, [href], [tabindex]:not([tabindex="-1"])'))
      .filter((element) => !element.disabled && this._isShown(element));
    if (!focusable.length) {
      event.preventDefault();
      this.dialog.focus();
      return;
    }
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    } else if (!this.dialog.contains(document.activeElement) || document.activeElement === this.dialog) {
      event.preventDefault();
      (event.shiftKey ? last : first).focus();
    }
  }

  /** Panels are hidden with visibility, so offsetParent says nothing here. */
  _isShown(element) {
    return !!element && element.getClientRects().length > 0 && window.getComputedStyle(element).visibility === 'visible';
  }

  /** The button the current state is about. */
  _primaryAction() {
    const state = this.dataset.state;
    if (state === 'won' && this.result && this.result.gift) {
      const actions = this.querySelector(`[data-gift-actions="${this._cssEscape(this.result.slice)}"]`);
      const add = actions && actions.querySelector('[data-action="add-wheel-gift"]');
      if (add && !add.disabled) return add;
      return actions ? actions.querySelector('a') : null;
    }
    if (state === 'won') {
      const apply = this.querySelector('[data-action="apply-wheel-code"]');
      if (apply && !apply.disabled) return apply;
      return this.querySelector('.cart-promo-popup__cta[data-action="copy-wheel-code"]');
    }
    if (state === 'lost') return this.querySelector('.wheel-popup__panel--lost [data-action="close-wheel"]');
    return this.querySelector('[data-action="spin-wheel"]');
  }

  _setState(state) {
    this.dataset.state = state;
  }

  /** "Spin again" mode: its head and the button's second label. */
  _setRetry(on) {
    if (on) {
      this.dataset.retry = 'true';
    } else {
      delete this.dataset.retry;
    }
    const button = this.querySelector('[data-action="spin-wheel"]');
    if (!button) return;
    const label = on ? button.dataset.againLabel : button.dataset.spinLabel;
    if (label) button.textContent = label;
  }

  /** The spin button and the close button sit out the spin. */
  _setSpinControls(enabled) {
    const spin = this.querySelector('[data-action="spin-wheel"]');
    const close = this.querySelector('.tombola-popup__close');
    // A focused button that gets disabled would drop the focus to the page.
    if (!enabled && this.dialog && [spin, close].includes(document.activeElement)) {
      this.dialog.focus({ preventScroll: true });
    }
    if (spin) spin.disabled = !enabled;
    if (close) close.disabled = !enabled;
  }

  _announce(text) {
    const live = this.querySelector('[data-wheel-live]');
    if (!live) return;
    live.textContent = '';
    // A short gap makes screen readers read a repeated text again.
    window.setTimeout(() => {
      live.textContent = (text || '').replace(/\s+/g, ' ').trim();
    }, 50);
  }

  /* ------------------------------------------------------ sizing ------ */

  /**
   * The wheel takes the height the popup has left: the script adds up what
   * the rest of the wheel's column needs at this width and hands the remainder
   * to the stylesheet (--wheel-room), which still caps it by the width. Only
   * the blocks that share the column with the wheel count: a taller result
   * panel makes the popup scroll, a smaller wheel would not help it.
   */
  _fitWheel() {
    const stage = this.querySelector('.wheel-popup__stage');
    if (!this.dialog || !this.wheelWrap || !stage) return;
    const style = window.getComputedStyle(this.dialog);
    const px = (value) => parseFloat(value) || 0;
    const available = this.clientHeight - px(style.marginTop) - px(style.marginBottom);
    const maxHeight = parseFloat(style.maxHeight);
    const limit = Number.isFinite(maxHeight) ? Math.min(available, maxHeight) : available;
    if (!(limit > 0)) return;

    const stageStyle = window.getComputedStyle(stage);
    let used = px(style.borderTopWidth) + px(style.borderBottomWidth) + px(style.paddingTop) + px(style.paddingBottom) +
      px(stageStyle.paddingTop) + px(stageStyle.paddingBottom);
    const twoColumns = style.gridTemplateColumns.trim().split(/\s+/).length > 1;
    if (!twoColumns) {
      const outer = (element) => {
        if (!element) return 0;
        const own = window.getComputedStyle(element);
        return element.offsetHeight + px(own.marginTop) + px(own.marginBottom);
      };
      used += outer(this.querySelector('.wheel-popup__head')) +
        outer(this.querySelector('.wheel-popup__panel--intro')) +
        outer(this.querySelector('.wheel-popup__body > .tombola-popup__terms'));
    }
    this.style.setProperty('--wheel-room', `${Math.floor(limit - used)}px`);
    this._fitResultWheel(this.dataset.state === 'lost' ? 'lost' : 'won');
  }

  /**
   * One column: on the result the wheel shrinks into the top of the popup, still
   * showing where it stopped, and the result sits under it. It is a transform
   * only and the result panel lies over the layout, so the popup keeps its
   * size; the scale leaves room for this result (won or lost) above the
   * buttons. Measured after the prize texts are in: the panels are laid out
   * even while hidden.
   */
  _fitResultWheel(kind) {
    const names = ['--wheel-result-scale', '--wheel-result-shift', '--wheel-result-top', '--wheel-result-bottom'];
    const head = this.querySelector('.wheel-popup__head');
    const intro = this.querySelector('.wheel-popup__panel--intro');
    const panel = this.querySelector(`.wheel-popup__panel--${kind}`);
    const size = this.wheelWrap ? this.wheelWrap.offsetWidth : 0;
    const twoColumns = window.getComputedStyle(this.dialog).gridTemplateColumns.trim().split(/\s+/).length > 1;
    this._reserveResultHeight(twoColumns);
    if (twoColumns || !head || !intro || !panel || !size) {
      names.forEach((name) => this.style.removeProperty(name));
      return;
    }
    const px = (value) => parseFloat(value) || 0;
    const top = px(window.getComputedStyle(head).paddingTop);
    const bottom = intro.offsetTop + intro.offsetHeight;
    this.style.setProperty('--wheel-result-bottom', `${Math.max(0, Math.round(this.dialog.clientHeight - bottom))}px`);
    // The prize (product card or slice image) shows instead of the shrunken wheel.
    if (kind === 'won' && this.classList.contains('has-prize-visual')) {
      this.style.setProperty('--wheel-result-top', `${Math.round(top)}px`);
      return;
    }
    const result = panel.querySelector('.wheel-popup__result');
    const actions = panel.querySelector('.tombola-popup__actions');
    const content = (result ? result.offsetHeight : 0) + (actions ? actions.offsetHeight : 0) + px(window.getComputedStyle(panel).paddingBottom);
    const scale = Math.max(RESULT_WHEEL_MIN_SCALE, Math.min(RESULT_WHEEL_MAX_SCALE, (bottom - top - RESULT_WHEEL_GAP - content) / size));
    this.style.setProperty('--wheel-result-scale', scale.toFixed(3));
    this.style.setProperty('--wheel-result-shift', `${Math.round(top - this.wheelWrap.offsetTop)}px`);
    this.style.setProperty('--wheel-result-top', `${Math.round(top + size * scale + RESULT_WHEEL_GAP)}px`);
  }

  /**
   * Two columns: a won result (prize name, card or image, gift text, code) can
   * be taller than the wheel. The popup takes the tallest one's height from the
   * start, so the reveal never grows it: every slice that can win is laid out
   * in the hidden panel once, with its own texts, and measured.
   */
  _reserveResultHeight(twoColumns) {
    const panel = this.querySelector('.wheel-popup__panel--won');
    const winners = this.slices.filter((slice) => ['prize', 'gift'].includes(this._kind(slice)));
    if (!twoColumns || !panel || !winners.length) {
      this.dialog.style.minHeight = '';
      return;
    }
    const result = panel.querySelector('.wheel-popup__result');
    const title = panel.querySelector('[data-prize-title]');
    const chip = panel.querySelector('[data-prize-code]');
    const toggles = Array.from(panel.querySelectorAll('[data-prize-visual], [data-gift-text], [data-gift-actions], [data-code-actions]'));
    const saved = [...toggles, chip].filter(Boolean).map((element) => [element, element.hidden]);
    const message = panel.querySelector('.wheel-popup__msg');
    const savedTitle = title ? title.textContent : '';
    const savedCode = chip ? chip.textContent : '';
    const savedMessage = message ? message.dataset.msg : '';
    const panelStyle = window.getComputedStyle(panel);
    const padding = (parseFloat(panelStyle.paddingTop) || 0) + (parseFloat(panelStyle.paddingBottom) || 0);
    let tallest = 0;
    winners.forEach((slice) => {
      const gift = this._kind(slice) === 'gift';
      toggles.forEach((element) => {
        if (element.dataset.prizeVisual !== undefined) element.hidden = element.dataset.prizeVisual !== slice.id;
        else if (element.dataset.giftText !== undefined) element.hidden = !gift || element.dataset.giftText !== slice.id;
        else if (element.dataset.giftActions !== undefined) element.hidden = !gift || element.dataset.giftActions !== slice.id;
        else element.hidden = gift;
      });
      if (title) title.textContent = slice.title;
      if (chip) {
        chip.textContent = slice.code;
        chip.hidden = !slice.code;
      }
      // A gift has no hint line (see _showResult), a code has.
      if (message) message.dataset.msg = gift ? 'gift' : 'info';
      const actions = Array.from(panel.querySelectorAll('.tombola-popup__actions')).reduce((height, element) => Math.max(height, element.offsetHeight), 0);
      tallest = Math.max(tallest, padding + (result ? result.offsetHeight : 0) + actions);
    });
    saved.forEach(([element, hidden]) => { element.hidden = hidden; });
    if (title) title.textContent = savedTitle;
    if (chip) chip.textContent = savedCode;
    if (message) message.dataset.msg = savedMessage;
    // Never past the popup's own maximum: min-height would beat max-height.
    const dialogStyle = window.getComputedStyle(this.dialog);
    const limit = this.clientHeight - (parseFloat(dialogStyle.marginTop) || 0) - (parseFloat(dialogStyle.marginBottom) || 0);
    this.dialog.style.minHeight = `${Math.ceil(limit > 0 ? Math.min(tallest, limit) : tallest)}px`;
  }

  _scheduleFit() {
    if (this._fitFrame) return;
    this._fitFrame = window.requestAnimationFrame(() => {
      this._fitFrame = null;
      if (this.getAttribute('aria-hidden') === 'false') this._fitWheel();
    });
  }

  /** The labels are fitted to the drawn size: a new size refits them. */
  _observeSize() {
    if (!('ResizeObserver' in window) || !this.wheelWrap) return;
    this._sizeObserver = new ResizeObserver(() => {
      window.clearTimeout(this._refitTimer);
      this._refitTimer = window.setTimeout(() => this._maybeRefit(), REFIT_DEBOUNCE_MS);
    });
    this._sizeObserver.observe(this.wheelWrap);
    // The texts wrap differently at another width: the room for the wheel changes.
    const head = this.querySelector('.wheel-popup__head');
    if (head) this._sizeObserver.observe(head);
  }

  _maybeRefit() {
    if (this.getAttribute('aria-hidden') === 'false') this._scheduleFit();
    if (!this.svgRoot || !this.wheelElement) return;
    const width = this.wheelElement.offsetWidth;
    if (!width || Math.abs(width - (this._fittedWidth || 0)) <= REFIT_MIN_CHANGE_PX) return;
    if (this.isSpinning) {
      this._refitPending = true;
      return;
    }
    this._refitPending = false;
    this._drawLabels();
  }

  /* ------------------------------------------------ theme editor ------ */

  _handleSectionSelect(event) {
    if (!event.detail || event.detail.sectionId !== this.options.sectionId) return;
    // Never cut a spin short: the editor would be left half way.
    if (this.isSpinning || this.dataset.state === 'landed') return;
    // Every preview starts over: nothing is stored in the editor.
    this._resetPreview();
    this.open();
  }

  _handleSectionDeselect(event) {
    if (event.detail && event.detail.sectionId !== this.options.sectionId) return;
    this._resetPreview();
    this.close();
  }

  _resetPreview() {
    this._stopSpin();
    window.clearTimeout(this._veilTimer);
    window.clearTimeout(this._feedbackTimer);
    this._setSpinControls(true);
    this.rotation = 0;
    if (this.rotor) {
      this.rotor.style.transition = 'none';
      this.rotor.style.transform = 'rotate(0deg)';
    }
    this._clearWinner();
    if (this.pointer) this.pointer.style.transform = '';
    this.result = null;
    this.isSpun = false;
    this.isBusy = false;
    this.progress = { retries: 0, last: null };
    this._setRetry(false);
    this._setApplied(false);
    this._setCopied(false);
    this.querySelectorAll('[data-action="add-wheel-gift"]').forEach((button) => this._setAddedState(button, false));
    this._pendingProductAdded = false;
    this._setMessage('info');
    this._setState('intro');
  }

  /* ------------------------------------------------------- draw ------- */

  _buildWheel() {
    // No slices yet (a new section in the editor): nothing to draw.
    if (!this.wheelElement || !this.slices.length) return;
    // After an editor re-render the old drawing is gone.
    if (this._wheelBuilt && this.wheelElement.querySelector('svg')) return;
    this._wheelBuilt = true;
    this.wheelElement.textContent = '';
    const oldFrame = this.querySelector('.wheel-popup__frame');
    if (oldFrame) oldFrame.remove();

    const count = this.slices.length;
    const repeat = count < 4 ? Math.ceil(MIN_SEGMENTS / count) : 1;
    // segments[j] = the slice drawn at position j, clockwise from the pointer.
    this.segments = [];
    for (let r = 0; r < repeat; r++) {
      for (let index = 0; index < count; index++) this.segments.push(index);
    }
    const segmentCount = this.segments.length;
    const segAngle = 360 / segmentCount;
    this.segAngle = segAngle;
    this.segmentColors = this._segmentColors();

    const svg = this._svg('svg', { viewBox: `0 0 ${VIEWBOX} ${VIEWBOX}`, class: 'wheel-popup__svg', focusable: 'false' });
    const sliceGroup = this._svg('g');
    const labelGroup = this._svg('g');
    const veils = this._svg('g');

    this.segments.forEach((sliceIndex, position) => {
      const start = position * segAngle - segAngle / 2;
      const path = this._slicePath(start, start + segAngle);
      sliceGroup.appendChild(this._svg('path', { d: path, fill: this.segmentColors[position], class: 'wheel-popup__slice' }));
      // Each label is laid out pointing straight up, then turned into place.
      labelGroup.appendChild(this._svg('g', { transform: `rotate(${position * segAngle} ${CENTER} ${CENTER})`, 'data-seg-content': position }));
      veils.appendChild(this._svg('path', { d: path, class: 'wheel-popup__veil', 'data-seg': position }));
    });

    svg.appendChild(sliceGroup);
    svg.appendChild(labelGroup);
    svg.appendChild(veils);
    this.wheelElement.appendChild(svg);
    this.svgRoot = svg;
    this._drawLabels();

    // Rim, keyline and pegs do not turn with the slices.
    const frame = this._svg('svg', { viewBox: `0 0 ${VIEWBOX} ${VIEWBOX}`, class: 'wheel-popup__frame', 'aria-hidden': 'true', focusable: 'false' });
    frame.appendChild(this._svg('circle', { cx: CENTER, cy: CENTER, r: 96, 'stroke-width': 8, class: 'wheel-popup__rim' }));
    frame.appendChild(this._svg('circle', { cx: CENTER, cy: CENTER, r: 92.4, class: 'wheel-popup__keyline' }));
    const bulbs = this._svg('g', { class: 'wheel-popup__bulbs' });
    const bulbCount = segmentCount * Math.max(2, Math.ceil(12 / segmentCount));
    for (let i = 0; i < bulbCount; i++) {
      const point = this._polar(96, (360 / bulbCount) * i);
      bulbs.appendChild(this._svg('circle', { cx: point.x.toFixed(3), cy: point.y.toFixed(3), r: 1.7 }));
    }
    frame.appendChild(bulbs);
    this.wheelElement.insertAdjacentElement('afterend', frame);

    // The whole disc turns as an HTML box: no SVG transform-origin quirks.
    this.rotor = this.wheelElement;
    this.rotor.style.transform = `rotate(${this.rotation}deg)`;
  }

  /** Labels and images of every segment, fitted to the wheel's drawn size. */
  _drawLabels() {
    if (!this.svgRoot || !this.segments) return;
    const segmentCount = this.segments.length;
    const segAngle = this.segAngle;
    const layout = this._sliceLayout(segmentCount, segAngle);
    this._fittedWidth = (this.wheelElement && this.wheelElement.offsetWidth) || 0;
    const rowSizes = this._evenRowSizes(this.slices.map((slice) =>
      slice.label.length ? this._fitRows(slice.label, layout, !!slice.image) : null));

    this.svgRoot.querySelectorAll('[data-seg-content]').forEach((group) => {
      const position = Number(group.getAttribute('data-seg-content'));
      const slice = this.slices[this.segments[position]];
      if (!slice) return;
      group.textContent = '';
      const color = this._labelColor(this.segmentColors[position]);
      const hasImage = !!slice.image;

      let image = null;
      const sizes = rowSizes[this.segments[position]];
      const label = sizes
        ? this._rowsLabel(slice.label, color, layout, hasImage, sizes)
        : this._sliceLabel(slice.title, color, layout, hasImage);
      if (hasImage) {
        // Two lines of text push the image towards the hub, and shrink it a
        // little when it would run under the hub.
        const room = label.lowestRadius - 3 - HUB_CLEARANCE;
        const size = room < layout.imageSize ? Math.max(room, layout.imageSize * 0.75) : layout.imageSize;
        image = { size, radius: Math.min(layout.imageRadius, label.lowestRadius - size / 2 - 3) };
      }

      if (image) {
        const cy = CENTER - image.radius;
        const clipId = `wheel-clip-${this.options.sectionId}-${position}`;
        const clip = this._svg('clipPath', { id: clipId, clipPathUnits: 'userSpaceOnUse' });
        clip.appendChild(this._svg('circle', { cx: CENTER, cy, r: image.size / 2 }));
        group.appendChild(clip);
        group.appendChild(this._svg('circle', { cx: CENTER, cy, r: image.size / 2 + 1.2, class: 'wheel-popup__slice-image-bg' }));
        const element = this._svg('image', {
          href: slice.image,
          x: CENTER - image.size / 2,
          y: cy - image.size / 2,
          width: image.size,
          height: image.size,
          preserveAspectRatio: 'xMidYMid slice',
          'clip-path': `url(#${clipId})`,
          class: 'wheel-popup__slice-image'
        });
        element.addEventListener('load', () => element.classList.add('is-loaded'), { once: true });
        group.appendChild(element);
      }
      group.appendChild(label.element);
    });
  }

  /** Font size, label and image positions for the number of segments. */
  _sliceLayout(count, segAngle) {
    const table = count <= 4 ? 10 : count <= 6 ? 9 : count <= 8 ? 8.5 : count <= 10 ? 8 : 7.5;
    // Never below 10 CSS pixels on the drawn wheel.
    const wheelWidth = (this.wheelElement && this.wheelElement.offsetWidth) || 300;
    const minSize = (10 * VIEWBOX) / Math.max(1, wheelWidth);
    const halfAngle = (Math.min(segAngle, 120) / 2) * (Math.PI / 180);
    const imageRadius = SLICE_RADIUS * 0.47;
    const narrow = count >= NARROW_FROM_SEGMENTS;
    return {
      fontSize: Math.max(table, minSize),
      minSize,
      halfAngle,
      narrow,
      // Narrow slices: the first line right under the pointer's tip.
      textRadius: SLICE_RADIUS * (narrow ? 0.84 : 0.8),
      imageRadius,
      imageSize: Math.min(2 * imageRadius * Math.sin(halfAngle) * 0.72, 32)
    };
  }

  /** Width a line may take at a distance from the centre (inside the slice edges). */
  _chordWidth(radius, layout) {
    return Math.max(0, Math.min(2 * radius * Math.sin(layout.halfAngle) * 0.84, 74));
  }

  _sliceLabel(title, color, layout, hasImage) {
    const maxLines = hasImage ? 2 : 3;
    const fitted = this._fitLabel(title, layout, hasImage, maxLines);
    const { fontSize, lines } = fitted;
    const text = this._svg('text', {
      x: CENTER,
      'text-anchor': 'middle',
      fill: color,
      'font-size': fontSize.toFixed(2),
      class: 'wheel-popup__slice-label'
    });

    lines.forEach((line, index) => {
      const tspan = this._svg('tspan', { x: CENTER, y: (CENTER - this._baselineRadius(index, fontSize, layout, hasImage)).toFixed(2) });
      tspan.textContent = line;
      text.appendChild(tspan);
    });
    return {
      element: text,
      lowestRadius: this._baselineRadius(Math.max(0, lines.length - 1), fontSize, layout, hasImage) - fontSize * 0.3
    };
  }

  /** Distance from the centre of the n-th line's baseline. */
  _baselineRadius(line, fontSize, layout, hasImage) {
    // Without an image the label can sit closer to the middle of a wide slice.
    return layout.textRadius - fontSize * (hasImage || layout.narrow ? 0.35 : 1.4) - line * fontSize * 1.08;
  }

  /**
   * One line if it fits, else the most even split over two (or three) lines,
   * then a smaller size, and an ellipsis only when nothing else works. Glyphs
   * are never squeezed.
   */
  _fitLabel(title, layout, hasImage, maxLines) {
    const words = title.trim().split(/\s+/).filter(Boolean);
    if (!words.length) return { fontSize: layout.fontSize, lines: [] };
    const floor = Math.max(layout.minSize, layout.fontSize * 0.75);
    const fits = (lines, size) => lines.every((line, index) =>
      this._textWidth(line, size) <= this._chordWidth(this._baselineRadius(index, size, layout, hasImage), layout));

    for (let size = layout.fontSize; size >= floor - 0.001; size -= 0.5) {
      for (let lineCount = 1; lineCount <= Math.min(maxLines, words.length); lineCount++) {
        const lines = this._bestSplit(words, lineCount, size);
        if (fits(lines, size)) return { fontSize: size, lines };
      }
    }

    // Still too long: the smallest size, the most even split, trimmed with an ellipsis.
    const size = floor;
    const lines = this._bestSplit(words, Math.min(maxLines, words.length), size);
    return {
      fontSize: size,
      lines: lines.map((line, index) => this._ellipsize(line, size, this._chordWidth(this._baselineRadius(index, size, layout, hasImage), layout)))
    };
  }

  _ellipsize(line, size, width) {
    if (this._textWidth(line, size) <= width) return line;
    let trimmed = line;
    while (trimmed.length > 1 && this._textWidth(`${trimmed}…`, size) > width) trimmed = trimmed.slice(0, -1).trimEnd();
    return `${trimmed}…`;
  }

  /** Distance from the centre of each row's baseline: the first row large, the others small. */
  _rowBaselines(rowCount, big, small, layout, hasImage) {
    // The first row starts where the title's first line would.
    const top = this._baselineRadius(0, layout.fontSize, layout, hasImage) + layout.fontSize * 0.74;
    const lines = [top - big * 0.74];
    for (let index = 1; index < rowCount; index++) {
      lines.push(lines[index - 1] - (index === 1 ? big * 0.22 + small * 0.95 : small * 1.08));
    }
    return lines;
  }

  _rowsLowest(lines, big, small) {
    return lines[lines.length - 1] - (lines.length > 1 ? small : big) * 0.3;
  }

  /**
   * The slice's own text on the wheel, row by row: the largest first row (a
   * value such as -10%, or a name) over small rows. Rows are never wrapped; a
   * row too long for the slice is trimmed.
   */
  _fitRows(rows, layout, hasImage) {
    const floor = layout.minSize;
    const lowestLimit = HUB_CLEARANCE + 3 + (hasImage ? layout.imageSize * 0.75 + 3 : 0);
    const fits = (big, small, trimSmall) => {
      const lines = this._rowBaselines(rows.length, big, small, layout, hasImage);
      return this._rowsLowest(lines, big, small) >= lowestLimit && rows.every((row, index) =>
        (trimSmall && index > 0) || this._textWidth(row, index ? small : big) <= this._chordWidth(lines[index], layout));
    };
    const largest = (small, minBig, trimSmall) => {
      for (let big = layout.fontSize * 2.2; big >= minBig - 0.001; big -= 0.5) {
        if (fits(big, small, trimSmall)) return big;
      }
      return null;
    };

    // The small rows may go a little under the usual minimum before they are trimmed.
    const smallFloor = floor * 0.9;
    const smallSizes = [];
    for (let size = Math.max(smallFloor, layout.fontSize * 0.85); size > smallFloor; size -= 0.5) smallSizes.push(size);
    smallSizes.push(smallFloor);

    // The first row clearly larger than the others; then all of one size; then trimmed.
    for (let index = 0; index < smallSizes.length; index++) {
      const big = largest(smallSizes[index], smallSizes[index] * 1.1, false);
      if (big !== null) return { big, small: smallSizes[index], rows: rows.length };
    }
    for (let index = 0; index < smallSizes.length; index++) {
      if (fits(smallSizes[index], smallSizes[index], false)) return { big: smallSizes[index], small: smallSizes[index], rows: rows.length };
    }
    return { big: largest(smallFloor, smallFloor, true) || smallFloor, small: smallFloor, rows: rows.length };
  }

  /**
   * Fitted slice by slice, the sizes would jump around the wheel. Every value
   * (-10%, -50) takes one size, every name a step smaller, every small row one
   * size. Only ever smaller than fitted, so everything still fits.
   */
  _evenRowSizes(fitted) {
    const smallest = (list) => (list.length ? Math.min(...list) : Infinity);
    const isValue = (index) => /\d/.test(this.slices[index].label[0]);
    const entries = fitted.map((fit, index) => fit && { fit, value: isValue(index) }).filter(Boolean);
    if (!entries.length) return fitted;
    const values = smallest(entries.filter((entry) => entry.value).map((entry) => entry.fit.big));
    const names = Math.min(smallest(entries.filter((entry) => !entry.value).map((entry) => entry.fit.big)), values * NAME_TO_VALUE_MAX);
    const valueBig = Math.min(values, names * VALUE_TO_NAME_MAX);
    const small = smallest(entries.filter((entry) => entry.fit.rows > 1).map((entry) => entry.fit.small));
    entries.forEach(({ fit, value }) => {
      if (fit.rows > 1) fit.small = small;
      fit.big = Math.max(value ? valueBig : names, fit.rows > 1 ? fit.small : 0);
    });
    return fitted;
  }

  _rowsLabel(rows, color, layout, hasImage, sizes) {
    const { big, small } = sizes;
    const lines = this._rowBaselines(rows.length, big, small, layout, hasImage);
    const text = this._svg('text', {
      x: CENTER,
      'text-anchor': 'middle',
      fill: color,
      class: 'wheel-popup__slice-label'
    });
    rows.forEach((row, index) => {
      const size = index ? small : big;
      const tspan = this._svg('tspan', { x: CENTER, y: (CENTER - lines[index]).toFixed(2), 'font-size': size.toFixed(2) });
      tspan.textContent = this._ellipsize(row, size, this._chordWidth(lines[index], layout));
      text.appendChild(tspan);
    });
    return { element: text, lowestRadius: this._rowsLowest(lines, big, small) };
  }

  /** The split into lineCount lines whose longest line is the shortest. */
  _bestSplit(words, lineCount, size) {
    if (lineCount <= 1) return [words.join(' ')];
    let best = null;
    let bestWidth = Infinity;
    const walk = (start, remaining, acc) => {
      if (remaining === 1) {
        const lines = acc.concat(words.slice(start).join(' '));
        const widest = Math.max(...lines.map((line) => this._textWidth(line, size)));
        if (widest < bestWidth) {
          bestWidth = widest;
          best = lines;
        }
        return;
      }
      for (let end = start + 1; end <= words.length - remaining + 1; end++) {
        walk(end, remaining - 1, acc.concat(words.slice(start, end).join(' ')));
      }
    };
    walk(0, lineCount, []);
    return best || [words.join(' ')];
  }

  /** Measured with the page font when canvas is available. */
  _textWidth(text, fontSize) {
    if (this._measure === undefined) {
      try {
        const context = document.createElement('canvas').getContext('2d');
        const family = window.getComputedStyle(this.dialog || this).fontFamily || 'sans-serif';
        this._measure = context ? { context, family } : null;
      } catch (error) {
        this._measure = null;
      }
    }
    if (!this._measure) return text.length * fontSize * 0.6;
    // Measured at 100px and scaled, so fractional sizes stay exact.
    this._measure.context.font = `600 100px ${this._measure.family}`;
    return (this._measure.context.measureText(text).width * fontSize) / 100 + fontSize * 0.01 * text.length;
  }

  /**
   * Colors per prize (the same prize keeps its color in every repeat), then
   * no two equal colors and no two pale ones side by side, the seam included.
   */
  _segmentColors() {
    const base = this.slices.map((slice, index) => slice.color || PALETTE[index % PALETTE.length]);
    const colors = this.segments.map((index) => base[index]);
    const count = colors.length;
    const isPale = (color) => {
      const luminance = this._luminance(color);
      return luminance !== null && luminance > 0.5;
    };
    for (let position = 0; position < count; position++) {
      if (this.slices[this.segments[position]].color) continue;
      const previous = colors[(position - 1 + count) % count];
      const next = colors[(position + 1) % count];
      const current = colors[position];
      const clash = current === previous || current === next || (isPale(current) && (isPale(previous) || isPale(next)));
      if (!clash) continue;
      const replacement = PALETTE.find((color) => color !== previous && color !== next && !(isPale(color) && (isPale(previous) || isPale(next))));
      if (replacement) colors[position] = replacement;
    }
    return colors;
  }

  /** WCAG relative luminance, or null for a value that is not a hex color. */
  _luminance(color) {
    let hex = String(color || '').trim().replace(/^#/, '');
    if (/^[0-9a-f]{3}$/i.test(hex)) hex = hex.split('').map((c) => c + c).join('');
    if (!/^[0-9a-f]{6}$/i.test(hex)) return null;
    const value = parseInt(hex, 16);
    const channel = (shift) => {
      const c = ((value >> shift) & 255) / 255;
      return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    };
    return 0.2126 * channel(16) + 0.7152 * channel(8) + 0.0722 * channel(0);
  }

  /** White or navy, whichever contrasts more with the slice. */
  _labelColor(fill) {
    const luminance = this._luminance(fill);
    if (luminance === null) return TEXT_LIGHT;
    // 0.0634 = luminance of #12202b + 0.05
    return 1.05 / (luminance + 0.05) >= (luminance + 0.05) / 0.0634 ? TEXT_LIGHT : TEXT_DARK;
  }

  _polar(radius, angleDeg) {
    const radians = ((angleDeg - 90) * Math.PI) / 180;
    return { x: CENTER + radius * Math.cos(radians), y: CENTER + radius * Math.sin(radians) };
  }

  _slicePath(startDeg, endDeg) {
    const start = this._polar(SLICE_RADIUS, startDeg);
    const end = this._polar(SLICE_RADIUS, endDeg);
    const largeArc = endDeg - startDeg > 180 ? 1 : 0;
    return `M ${CENTER} ${CENTER} L ${start.x.toFixed(3)} ${start.y.toFixed(3)} A ${SLICE_RADIUS} ${SLICE_RADIUS} 0 ${largeArc} 1 ${end.x.toFixed(3)} ${end.y.toFixed(3)} Z`;
  }

  _svg(name, attributes = {}) {
    const element = document.createElementNS(SVG_NS, name);
    Object.keys(attributes).forEach((key) => element.setAttribute(key, attributes[key]));
    return element;
  }

  /* ------------------------------------------------------- spin ------- */

  _random() {
    if (window.crypto && window.crypto.getRandomValues) {
      return window.crypto.getRandomValues(new Uint32Array(1))[0] / 4294967296;
    }
    return Math.random();
  }

  _reduceMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  /** Weighted draw; "spin again" slices drop out once the extra spins are used up. */
  _drawIndex() {
    const allowRetry = this.progress.retries < this.options.maxRetries;
    const weights = this.slices.map((slice) => (this._kind(slice) === 'retry' && !allowRetry ? 0 : slice.weight));
    const total = weights.reduce((sum, weight) => sum + weight, 0);
    if (total <= 0) {
      // Only reachable in the editor preview (the wheel never opens otherwise):
      // any real outcome, so the preview still works.
      const outcomes = this.slices.map((slice, index) => index).filter((index) => this._kind(this.slices[index]) !== 'retry');
      const pool = outcomes.length ? outcomes : this.slices.map((slice, index) => index);
      return pool[Math.floor(this._random() * pool.length)];
    }
    let ticket = this._random() * total;
    for (let index = 0; index < this.slices.length; index++) {
      ticket -= weights[index];
      if (ticket < 0 && weights[index] > 0) return index;
    }
    // Floating point leftovers land on the last slice that can win.
    for (let index = this.slices.length - 1; index >= 0; index--) {
      if (weights[index] > 0) return index;
    }
    return 0;
  }

  _spin(event) {
    if (event) event.preventDefault();
    if (this.isSpinning || this.result || this.dataset.state === 'landed' || !this.rotor) return;
    // A click from Enter or Space has no pointer detail: only then focus follows the result.
    this._keyboardSpin = !!event && event.detail === 0;

    if (!this.designMode) {
      // Played in another tab meanwhile: that outcome stands, no second draw.
      const stored = this._readStoredResult();
      if (stored) {
        this.result = stored;
        this.isSpun = true;
        this._showResult(stored, false);
        return;
      }
      this.progress = this._readProgress();
    }

    const index = this._drawIndex();
    const slice = this.slices[index];
    if (!slice) return;
    const kind = this._kind(slice);

    if (kind === 'retry') {
      // Stored before the first frame: a reload owes exactly this one spin.
      this.progress = { retries: this.progress.retries + 1, last: slice.id };
      this._storeProgress();
      this._outcome = 'retry';
    } else {
      const won = kind === 'prize' || kind === 'gift';
      this.result = { slice: slice.id, index, title: slice.title, image: slice.image, code: won ? slice.code : '', at: Date.now() };
      if (kind === 'gift') {
        this.result.gift = true;
        this.result.productTitle = slice.productTitle;
      }
      // A prize is owed until it is used: a reload mid-spin, or a won screen left
      // without applying, copying or adding the gift, shows it once more on the next page load.
      if (this.result.code || this.result.gift) this.result.reopen = 'pending';
      this.isSpun = true;
      // Stored before the first frame of the animation.
      this._storeResult();
      this._saveToCart(this.result);
      if (this.result.code) this._enterGuard();
      this._outcome = this.result.code || this.result.gift ? 'won' : 'lost';
    }
    this._outcomeIndex = index;
    this.isSpinning = true;

    window.clearTimeout(this._veilTimer);
    this._clearWinner();
    this._setState('spinning');
    this._setSpinControls(false);
    this._announce(this.dataset.liveSpinning);
    if (this.dialog.scrollHeight > this.dialog.clientHeight + 1) {
      this.dialog.scrollTo({ top: 0 });
      this.wheelElement.scrollIntoView({ block: 'nearest', behavior: this._reduceMotion() ? 'auto' : 'smooth' });
    }

    const reduceMotion = this._reduceMotion();
    const overshoot = reduceMotion ? 0 : Math.min(5, this.segAngle * 0.12);
    const target = this._targetRotation(index, reduceMotion ? 0 : FULL_TURNS, overshoot);
    const from = this.rotation;
    const token = ++this._spinToken;

    const land = () => {
      if (token !== this._spinToken || !this.isSpinning) return;
      window.clearTimeout(this._spinTimer);
      this.rotor.style.transition = 'none';
      this.rotor.style.transform = `rotate(${target}deg)`;
      if (this._spinAnimation) {
        this._spinAnimation.cancel();
        this._spinAnimation = null;
      }
      this.rotation = target;
      this._onLanded();
    };

    this.rotor.style.transition = 'none';
    this.rotor.style.transform = `rotate(${from}deg)`;

    if (reduceMotion || typeof this.rotor.animate !== 'function') {
      this.rotor.style.transform = `rotate(${target}deg)`;
      window.requestAnimationFrame(land);
      // rAF does not run in a background tab.
      this._spinTimer = window.setTimeout(land, 120);
      return;
    }

    // Wind-up, a long deceleration, a small overshoot and the settle.
    this._spinAnimation = this.rotor.animate([
      { transform: `rotate(${from}deg)`, easing: 'cubic-bezier(.45,0,.55,1)' },
      { transform: `rotate(${from - 6}deg)`, offset: 0.04, easing: 'cubic-bezier(.22,.55,.18,1)' },
      { transform: `rotate(${target + overshoot}deg)`, offset: 0.92, easing: 'cubic-bezier(.45,0,.55,1)' },
      { transform: `rotate(${target}deg)` }
    ], { duration: SPIN_MS, fill: 'forwards' });
    this._spinAnimation.finished.then(land, () => {});
    // Animations stall in a background tab, so a timer backs them up.
    this._spinTimer = window.setTimeout(() => {
      if (this._spinAnimation && token === this._spinToken) this._spinAnimation.finish();
      land();
    }, SPIN_MS + 400);

    this._runFlapper(token);
  }

  /** Degrees before a peg where the pointer starts to bend. */
  _flapperContact() {
    return Math.min(FLAPPER_CONTACT_MAX_DEG, this.segAngle * 0.25);
  }

  /**
   * Clockwise turn that stops one of the drawn prize's segments under the
   * pointer, well inside its edges. Towards the next peg the stop keeps clear
   * of the pointer's contact zone (overshoot included), so it rests straight.
   */
  _targetRotation(index, turns, overshoot = 0) {
    const positions = this.segments.map((sliceIndex, position) => (sliceIndex === index ? position : -1)).filter((position) => position >= 0);
    const position = positions[Math.floor(this._random() * positions.length)] || 0;
    const segAngle = this.segAngle;
    const low = -0.3 * segAngle;
    const high = Math.max(0, Math.min(0.3 * segAngle, segAngle / 2 - this._flapperContact() - overshoot - 1));
    const jitter = low + this._random() * (high - low);
    const finalAngle = (((360 - position * segAngle + jitter) % 360) + 360) % 360;
    const current = ((this.rotation % 360) + 360) % 360;
    const delta = ((finalAngle - current) + 360) % 360;
    this._landedSegment = position;
    return this.rotation + turns * 360 + delta;
  }

  /** The pointer is pushed by every peg (segment edge) that passes under it. */
  _runFlapper(token) {
    if (!this.pointer) return;
    const segAngle = this.segAngle;
    const contact = this._flapperContact();
    let touching = false;
    const step = () => {
      if (token !== this._spinToken || !this.isSpinning) return;
      const angle = this._currentAngle();
      const toEdge = segAngle - ((angle + segAngle / 2) % segAngle);
      if (toEdge < contact) {
        touching = true;
        this.pointer.style.transition = 'none';
        this.pointer.style.transform = `rotate(${(-FLAPPER_MAX_DEG * (1 - toEdge / contact)).toFixed(2)}deg)`;
      } else if (touching) {
        touching = false;
        this._releasePointer();
      }
      window.requestAnimationFrame(step);
    };
    window.requestAnimationFrame(step);
  }

  _releasePointer() {
    if (!this.pointer) return;
    this.pointer.style.transition = 'transform 220ms cubic-bezier(.34,1.56,.64,1)';
    this.pointer.style.transform = 'rotate(0deg)';
  }

  _currentAngle() {
    const transform = window.getComputedStyle(this.rotor).transform;
    if (!transform || transform === 'none') return 0;
    const values = transform.match(/matrix\(([^)]+)\)/);
    if (!values) return 0;
    const [a, b] = values[1].split(',').map(Number);
    return ((Math.atan2(b, a) * 180) / Math.PI + 360) % 360;
  }

  /** Cancels a running spin and its timers (editor, removal). */
  _stopSpin() {
    this._spinToken++;
    if (this._spinAnimation) {
      this._spinAnimation.cancel();
      this._spinAnimation = null;
    }
    window.clearTimeout(this._spinTimer);
    window.clearTimeout(this._landTimer);
    this._landTimer = null;
    this.isSpinning = false;
  }

  /** The wheel stands still: the winner lights up for a moment, then the result. */
  _onLanded() {
    this.isSpinning = false;
    this._setState('landed');
    this._releasePointer();
    this._markWinner(this._landedSegment, false);
    if (this._refitPending) {
      this._refitPending = false;
      this._drawLabels();
    }
    window.clearTimeout(this._landTimer);
    this._landTimer = window.setTimeout(() => this._reveal(), this._reduceMotion() ? LANDED_HOLD_REDUCED_MS : LANDED_HOLD_MS);
  }

  _reveal() {
    window.clearTimeout(this._landTimer);
    this._landTimer = null;
    if (this.dataset.state !== 'landed') return;
    this._setSpinControls(true);
    if (this._outcome === 'retry') {
      this._showRetry(this._outcomeIndex, true);
    } else {
      this._showResult(this.result, true);
    }
  }

  _markWinner(position, instant) {
    if (!this.svgRoot || position === undefined || position === null) return;
    const veils = Array.from(this.svgRoot.querySelectorAll('.wheel-popup__veil'));
    if (instant) veils.forEach((veil) => { veil.style.transition = 'none'; });
    veils.forEach((veil) => veil.classList.toggle('is-winner', Number(veil.dataset.seg) === position));
    this.svgRoot.classList.add('is-landed');
    if (instant) {
      // Forces the instant change before the transition comes back.
      void this.svgRoot.getBoundingClientRect();
      veils.forEach((veil) => { veil.style.transition = ''; });
    }
  }

  _clearWinner() {
    if (!this.svgRoot) return;
    this.svgRoot.classList.remove('is-landed');
    this.svgRoot.querySelectorAll('.wheel-popup__veil.is-winner').forEach((veil) => veil.classList.remove('is-winner'));
  }

  /** Reopened later: the wheel shows where it stopped, without turning. */
  _restoreStop(sliceId) {
    const index = this.slices.findIndex((slice) => slice.id === sliceId);
    if (!this.rotor || index < 0 || !this.segments) return;
    const position = this.segments.indexOf(index);
    this.rotation = (360 - position * this.segAngle) % 360;
    this.rotor.style.transition = 'none';
    this.rotor.style.transform = `rotate(${this.rotation}deg)`;
    this._landedSegment = position;
    this._markWinner(position, true);
  }

  /** A "spin again" slice: the wheel stays where it stopped, the button spins once more. */
  _showRetry(index, announce) {
    if (!announce && this.progress.last) this._restoreStop(this.progress.last);
    this._setRetry(true);
    this._setState('intro');
    this._setSpinControls(true);
    // The other slices light up again: the next spin is a fresh one.
    window.clearTimeout(this._veilTimer);
    this._veilTimer = window.setTimeout(() => {
      if (!this.isSpinning && this.dataset.retry) this._clearWinner();
    }, RETRY_VEIL_MS);

    if (announce) {
      const head = this.querySelector('.wheel-popup__head-variant--retry');
      this._announce(head ? head.textContent : '');
      this._focusAfterReveal(() => this.querySelector('[data-action="spin-wheel"]'));
    }
  }

  /**
   * After a spin started from the keyboard the focus moves to the next action
   * once its panel has faded in; after a tap or a click it stays on the dialog,
   * so no focus ring shows on a button nobody tabbed to.
   */
  _focusAfterReveal(getTarget) {
    const keyboard = this._keyboardSpin;
    const focus = () => {
      if (this.getAttribute('aria-hidden') !== 'false') return true;
      const target = keyboard ? getTarget() : null;
      if (target && !target.disabled && this._isShown(target)) {
        target.focus({ preventScroll: true });
        target.scrollIntoView({ block: 'nearest' });
        return true;
      }
      if (!this.dialog.contains(document.activeElement)) this.dialog.focus({ preventScroll: true });
      return !keyboard;
    };
    if (!focus()) window.setTimeout(focus, 260);
  }

  _showResult(result, announce) {
    if (!result) return;
    const won = !!(result.code || result.gift);
    if (!announce && !this.isSpinning) this._restoreStop(result.slice);

    if (won) {
      const title = this.querySelector('[data-prize-title]');
      const code = this.querySelector('[data-prize-code]');
      if (title) title.textContent = result.title;
      if (code) {
        code.textContent = result.code || '';
        code.setAttribute('aria-label', `${code.dataset.srLabel || ''} ${result.code || ''}`.trim());
      }
      this._setWonVariant(result);
      if (result.gift) {
        this._setGiftAdded(!!result.applied);
        this._setMessage('gift');
      } else {
        this._setApplied(!!result.applied);
        this._setMessage(result.applied ? 'applied' : 'info');
      }
      // The prize name and the gift text can wrap: the shrunken wheel makes room for them.
      this._fitResultWheel('won');
    }

    if (!won) this._fitResultWheel('lost');
    if (announce && this.dialog) this.dialog.scrollTo({ top: 0 });
    this._setRetry(false);
    this._setState(won ? 'won' : 'lost');

    if (announce) {
      const kicker = this.querySelector('.wheel-popup__kicker');
      const lost = this.querySelector('.wheel-popup__panel--lost .wheel-popup__result');
      this._announce(won
        ? `${kicker ? kicker.textContent : ''} ${result.title}. ${result.code || ''}`
        : (lost ? lost.textContent : ''));
      this._focusAfterReveal(() => this._primaryAction());
    }
  }

  /**
   * The prize rides along to the order. Only the submitted keys change, the
   * other cart attributes (tombola consent, pickup prices) stay as they are.
   */
  async _saveToCart(result) {
    if (this.designMode || !this.options.attributeKey) return;
    let value = result.code ? `${result.title} | ${result.code}` : `${result.title} | -`;
    if (result.gift) value = `${result.title} | ${result.productTitle}${result.code ? ` | ${result.code}` : ''}`;
    try {
      await fetch(`${this._cartUrl()}/update.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({
          attributes: {
            [this.options.attributeKey]: value,
            _wheel_campaign: this.options.campaign,
            ...this._prizeAttributes(result)
          }
        })
      });
    } catch (error) {
      // The localStorage copy still blocks a second spin on this browser.
    }
  }

  /** The won code and gift variant: the cart drawer shows its delivery-only note while either is in the cart. */
  _prizeAttributes(result) {
    const giftButton = result.gift ? this.querySelector(`[data-gift-actions="${this._cssEscape(result.slice)}"] [data-variant-id]`) : null;
    return { _wheel_code: result.code || '', _wheel_gift: giftButton ? giftButton.dataset.variantId : '' };
  }

  async _savePrizeToCart(result) {
    const attributes = this._prizeAttributes(result);
    if (!attributes._wheel_code && !attributes._wheel_gift) return;
    try {
      await fetch(`${this._cartUrl()}/update.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ attributes })
      });
    } catch (error) {
      // Only the note is missing; the next page load tries again.
    }
  }

  /* ---------------------------------------------------- the code ------ */

  _setMessage(kind) {
    const message = this.querySelector('.wheel-popup__msg');
    if (message) message.dataset.msg = kind;
  }

  /** The cart promo popup's "added" state: green, disabled, second label. */
  _setApplied(applied) {
    const button = this.querySelector('[data-action="apply-wheel-code"]');
    if (!button) return;
    button.classList.toggle('is-added', applied);
    button.disabled = applied;
    const label = applied ? button.dataset.appliedLabel : button.dataset.applyLabel;
    if (label) button.textContent = label;
  }

  /** Code or gift: only the won slice's gift text and buttons, the chip only with a code. */
  _setWonVariant(result) {
    const gift = !!result.gift;
    this.querySelectorAll('[data-gift-text]').forEach((element) => {
      element.hidden = !gift || element.dataset.giftText !== result.slice;
    });
    this.querySelectorAll('[data-gift-actions]').forEach((element) => {
      element.hidden = !gift || element.dataset.giftActions !== result.slice;
    });
    const codeActions = this.querySelector('[data-code-actions]');
    if (codeActions) codeActions.hidden = gift;
    const chip = this.querySelector('[data-prize-code]');
    if (chip) chip.hidden = !result.code;
    let visual = null;
    this.querySelectorAll('[data-prize-visual]').forEach((element) => {
      element.hidden = element.dataset.prizeVisual !== result.slice;
      if (!element.hidden) visual = element;
    });
    this.classList.toggle('has-prize-visual', !!visual);
  }

  _cssEscape(value) {
    return window.CSS && CSS.escape ? CSS.escape(value) : String(value).replace(/["\\]/g, '\\$&');
  }

  _giftButton() {
    if (!this.result || !this.result.gift) return null;
    return this.querySelector(`[data-gift-actions="${this._cssEscape(this.result.slice)}"] [data-action="add-wheel-gift"]`);
  }

  /** The cart promo popup's "added" state on an add to cart button. */
  _setAddedState(button, added) {
    button.classList.toggle('is-added', added);
    button.disabled = added;
    const label = added ? button.dataset.addedLabel : button.dataset.addLabel;
    if (label) button.textContent = label;
  }

  _setGiftAdded(added) {
    const button = this._giftButton();
    if (button) this._setAddedState(button, added);
  }

  /**
   * The cart promo popup's add to cart: the gift goes in, then its code (a
   * discount that makes it free) when the slice has one.
   */
  async _addGift(event, target) {
    if (event) event.preventDefault();
    const button = target || this._giftButton();
    if (!button || !this.result || !this.result.gift || this.isBusy || button.classList.contains('is-added')) return;

    this.isBusy = true;
    const hadFocus = document.activeElement === button;
    if (hadFocus) this.dialog.focus({ preventScroll: true });
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    this._setMessage('gift');

    const added = await this._postToCart(Number(button.dataset.variantId));
    const codeApplied = added.ok && this.result.code ? await this._applyDiscount(this.result.code) : true;

    this.isBusy = false;
    button.removeAttribute('aria-busy');

    if (!added.ok) {
      button.disabled = false;
      const error = this.querySelector('[data-gift-error]');
      if (error) {
        if (!error.dataset.defaultText) error.dataset.defaultText = error.textContent;
        // Shopify explains a refused add (sold out, quantity limit) in `description`.
        const text = added.message || error.dataset.defaultText;
        error.textContent = '';
        // Written again once visible, so the alert is read out.
        window.setTimeout(() => { error.textContent = text; }, 50);
      }
      this._setMessage('gift-error');
      if (hadFocus) button.focus({ preventScroll: true });
      return;
    }

    this._setAddedState(button, true);
    this.result.applied = true;
    this._storeResult();
    this._pendingProductAdded = true;
    // A code Shopify refused: its error explains, the chip stays there to copy.
    this._setMessage(codeApplied ? 'gift' : 'error');
    this._announce(button.dataset.addedLabel || '');
    if (hadFocus) {
      const next = this._primaryAction();
      if (next) next.focus({ preventScroll: true });
    }
  }

  async _postToCart(variantId) {
    if (this.designMode) return { ok: true };
    const addUrl = window.routes && window.routes.cartAddUrl ? window.routes.cartAddUrl : `${this._cartRoot()}cart/add`;
    try {
      const response = await fetch(`${addUrl}.js`, {
        body: JSON.stringify({ id: variantId, quantity: 1 }),
        credentials: 'same-origin',
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
      });
      if (response.ok) return { ok: true };
      const content = await response.json().catch(() => ({}));
      return { ok: false, message: typeof content.description === 'string' ? content.description : '' };
    } catch (error) {
      return { ok: false, message: '' };
    }
  }

  /**
   * product:added updates the header count and opens the mini cart, so it is held
   * back until the popup closes (the cart promo popup does the same): the drawer
   * would otherwise slide in over the result.
   */
  _announceAddedProduct() {
    if (!this._pendingProductAdded) return;
    this._pendingProductAdded = false;
    this.dispatchEvent(new CustomEvent('product:added', {
      bubbles: true,
      detail: { quantity: 1, productAdded: true }
    }));
  }

  /** The cart promo popup's "copied" state on the chip and the button. */
  _setCopied(copied) {
    const chip = this.querySelector('.cart-promo-popup__code');
    const button = this.querySelector('.cart-promo-popup__cta[data-action="copy-wheel-code"]');
    if (chip) chip.classList.toggle('is-copied', copied);
    if (button) {
      button.classList.toggle('is-copied', copied);
      const label = copied ? button.dataset.copiedLabel : button.dataset.copyLabel;
      if (label) button.textContent = label;
    }
  }

  async _applyCode(event, target) {
    if (event) event.preventDefault();
    const button = target || this.querySelector('[data-action="apply-wheel-code"]');
    if (!this.result || !this.result.code || this.isBusy || button.classList.contains('is-added')) return;

    // Like the cart promo popup: disabled and aria-busy while the request runs.
    this.isBusy = true;
    const hadFocus = document.activeElement === button;
    if (hadFocus) this.dialog.focus({ preventScroll: true });
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    this._setMessage('info');

    const applied = await this._applyDiscount(this.result.code);

    this.isBusy = false;
    button.removeAttribute('aria-busy');

    if (!applied) {
      button.disabled = false;
      this._setMessage('error');
      // Written again once visible, so the alert is read out.
      const error = this.querySelector('[data-wheel-error]');
      if (error) {
        const text = error.textContent;
        error.textContent = '';
        window.setTimeout(() => { error.textContent = text; }, 50);
      }
      if (hadFocus) button.focus({ preventScroll: true });
      return;
    }

    this._setApplied(true);
    this._setMessage('applied');
    this.result.applied = true;
    this._storeResult();
    const appliedText = this.querySelector('.wheel-popup__msg-applied');
    this._announce(appliedText ? appliedText.textContent : '');
    if (hadFocus) {
      const copy = this._primaryAction();
      if (copy) copy.focus({ preventScroll: true });
    }

    // Same refresh as the discount field in the cart drawer.
    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
      bubbles: true,
      detail: { scrollToTop: false }
    }));
  }

  /**
   * /discount/<code> is the storefront's own route for codes (the cart drawer's
   * discount field uses it too); the cart read back tells whether it applies.
   */
  async _applyDiscount(code) {
    if (this.designMode) return true;
    try {
      await fetch(`${this._cartRoot()}discount/${encodeURIComponent(code)}`, {
        method: 'GET',
        credentials: 'same-origin',
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        redirect: 'manual'
      });
      if (this._codeApplied(await this._fetchCart(), code)) return true;
      await new Promise((resolve) => window.setTimeout(resolve, CODE_RECHECK_MS));
      return this._codeApplied(await this._fetchCart(), code);
    } catch (error) {
      return false;
    }
  }

  /**
   * Applicable, or already taken off a line or the cart total: a gift's product
   * discount can be on its line while discount_codes still says not applicable.
   */
  _codeApplied(cart, code) {
    if (!cart) return false;
    const wanted = code.toLowerCase();
    const same = (value) => String(value || '').toLowerCase() === wanted;
    const codes = Array.isArray(cart.discount_codes) ? cart.discount_codes : [];
    if (codes.some((entry) => entry && same(entry.code) && entry.applicable)) return true;
    const items = Array.isArray(cart.items) ? cart.items : [];
    const onLine = items.some((item) => (item.line_level_discount_allocations || [])
      .some((allocation) => allocation && allocation.discount_application && same(allocation.discount_application.title)));
    const onCart = (cart.cart_level_discount_applications || []).some((application) => application && same(application.title));
    return onLine || onCart;
  }

  async _copyCode(event) {
    if (event) event.preventDefault();
    if (!this.result || !this.result.code) return;

    const copied = await this._writeToClipboard(this.result.code);
    if (!copied) {
      const value = this.querySelector('[data-prize-code]');
      if (value) this._selectText(value);
      return;
    }

    this._setCopied(true);
    if (!this.result.copied) {
      this.result.copied = true;
      this._storeResult();
    }
    const button = this.querySelector('.cart-promo-popup__cta[data-action="copy-wheel-code"]');
    this._announce(button ? button.dataset.copiedLabel : '');
    window.clearTimeout(this._feedbackTimer);
    this._feedbackTimer = window.setTimeout(() => this._setCopied(false), COPIED_FEEDBACK_MS);
  }

  async _writeToClipboard(text) {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      try {
        await navigator.clipboard.writeText(text);
        return true;
      } catch (error) {
        // Refused in the theme editor iframe and on http: try the old way.
      }
    }
    return this._legacyCopy(text);
  }

  /** The cart promo popup's fallback, as is: it puts focus and selection back. */
  _legacyCopy(text) {
    const previousActiveElement = document.activeElement;
    const selection = window.getSelection();
    const previousRange = selection && selection.rangeCount > 0 ? selection.getRangeAt(0) : null;

    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('aria-hidden', 'true');
    textarea.setAttribute('tabindex', '-1');
    textarea.style.cssText = 'position:fixed;top:0;left:0;width:1px;height:1px;padding:0;border:0;outline:0;opacity:0;';
    this.dialog.appendChild(textarea);

    let copied = false;
    try {
      if (this._isIOS()) {
        // iOS ignores textarea.select() - it needs an explicit range plus
        // setSelectionRange on an editable field.
        textarea.contentEditable = 'true';
        textarea.readOnly = false;
        const range = document.createRange();
        range.selectNodeContents(textarea);
        if (selection) {
          selection.removeAllRanges();
          selection.addRange(range);
        }
      }
      textarea.focus({ preventScroll: true });
      textarea.setSelectionRange(0, text.length);
      copied = document.execCommand('copy');
    } catch (error) {
      copied = false;
    }

    textarea.remove();

    if (selection) {
      selection.removeAllRanges();
      if (previousRange) selection.addRange(previousRange);
    }
    if (previousActiveElement && typeof previousActiveElement.focus === 'function') {
      previousActiveElement.focus({ preventScroll: true });
    }

    return copied;
  }

  _isIOS() {
    return /iP(ad|hone|od)/.test(navigator.userAgent)
      || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  }

  _selectText(element) {
    const selection = window.getSelection();
    if (!selection) return;
    const range = document.createRange();
    range.selectNodeContents(element);
    selection.removeAllRanges();
    selection.addRange(range);
  }
}

if (!window.customElements.get('cos-wheel-of-fortune')) {
  window.customElements.define('cos-wheel-of-fortune', WheelOfFortuneSection);
}
